import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSmartToy, MdPerson, MdCheck, MdClose, MdSearch, MdSchedule, MdInfo, MdAutoAwesome, MdSend, MdSettings, MdAdd, MdPeople, MdAutoGraph, MdBarChart, MdVisibility, MdDragIndicator, MdRocket, MdExpandMore, MdExpandLess, MdKeyboardArrowDown, MdRemove } from 'react-icons/md';
import { doc, getDoc, updateDoc, deleteDoc, collection, getDocs, query, orderBy, setDoc, serverTimestamp, onSnapshot, deleteField } from 'firebase/firestore';
import { db, functions } from '@config/firebase';
import notificationService from '@services/notificationService';
import { createMentorshipMatches, generateEmails, cancelEmails } from '@services/cloudFunctions';
import { httpsCallable } from 'firebase/functions';
import { useTenant } from '@contexts/TenantContext';
import { CustomLabels } from '@services/tenantsService';
import AlertPopup from 'components/popup/AlertPopup';
import ConfirmPopup from 'components/popup/ConfirmPopup';
import Popup from 'components/popup';
import { userDataService } from '@services/userDataService';
import { EnhancedUser } from 'types/profile.types';
import Avatar from 'components/avatar/Avatar';
import UserProfileModal from 'components/modal/UserProfileModal';
import {
  SAMPLE_RECOMMENDATIONS,
  SAMPLE_UNMATCHED_MENTEES,
  SAMPLE_UNMATCHED_MENTORS,
  SAMPLE_DISTRIBUTION_METRICS,
  EXAMPLE_COHORT,
  SAMPLE_PAIRS
} from './engine/data';
import { MentorshipPair, Cohort, CohortAction, PresetAction } from './engine/types';
import { convertUserToEnhancedUser, getCompatibilityLabel, EXAMPLE_COHORT_ID, getCohortLockState, getActionStepLabel } from './engine/utils';

import { ActionStatusBar, CohortRoadmap, LockBanner } from './engine/components';

const AIIntros = () => {
  const [pairs, setPairs] = useState<MentorshipPair[]>([]);
  const [recommendedSearch, setRecommendedSearch] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [unmatchedMentees, setUnmatchedMentees] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [unmatchedMentors, setUnmatchedMentors] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [distributionMetrics, setDistributionMetrics] = useState<any>(null);
  const [showCustomAttributes, setShowCustomAttributes] = useState(false);

  // Popup states
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    message: '',
    title: 'Alert',
    variant: 'info' as 'success' | 'warning' | 'info' | 'error',
  });
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    message: '',
    title: 'Confirm',
    onConfirm: () => {},
  });
  const [promptConfig, setPromptConfig] = useState({
    isOpen: false,
    title: 'Input Required',
    message: '',
    defaultValue: '',
    onConfirm: (_value: string) => {},
  });
  const [promptValue, setPromptValue] = useState('');

  const { currentTenantId } = useTenant();
  const navigate = useNavigate();

  // Cohort selection state
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [selectedCohortId, setSelectedCohortId] = useState<string | null>(() => {
    const saved = localStorage.getItem('selectedCohortId');
    if (saved === EXAMPLE_COHORT_ID || saved === '__EXAMPLE_COHORT__') {
      localStorage.removeItem('selectedCohortId');
      return null;
    }
    return saved || null;
  });
  const [isLoadingCohorts, setIsLoadingCohorts] = useState(false);
  const [openStepPopup, setOpenStepPopup] = useState<string | null>(null);

  // Participant selection state
  const [allUsers, setAllUsers] = useState<EnhancedUser[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<EnhancedUser[]>([]);
  const [pendingParticipants, setPendingParticipants] = useState<EnhancedUser[]>([]); // Temporary state for popup
  const [participantSearch, setParticipantSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [viewingUser, setViewingUser] = useState<EnhancedUser | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [hasLoadedInitialParticipants, setHasLoadedInitialParticipants] = useState(false);
  const hasLoadedParticipantsRef = useRef(false);
  const popupMouseDownOnBackdropRef = useRef(false);
  const [isGeneratingMatches, setIsGeneratingMatches] = useState(false);
  const [matchingStep, setMatchingStep] = useState<string | null>(null);
  const [matchingStartedAt, setMatchingStartedAt] = useState<Date | null>(null);
  const [matchingElapsed, setMatchingElapsed] = useState(0);
  const matchingNotifiedRef = useRef<string | null>(null);
  const emailGenNotifiedRef = useRef<string | null>(null);
  const emailGenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [exampleEmail, setExampleEmail] = useState<any>(null);
  const [isLoadingConnectionPreview, setIsLoadingConnectionPreview] = useState(false);
  const [isGeneratingEmails, setIsGeneratingEmails] = useState(false);
  const [launchHoldProgress, setLaunchHoldProgress] = useState(0);
  const [isHoldingLaunch, setIsHoldingLaunch] = useState(false);
  const [isExampleEmailExpanded, setIsExampleEmailExpanded] = useState(true);
  const [scheduledSendAction, setScheduledSendAction] = useState<CohortAction | null>(null);
  const [feedbackMaterial, setFeedbackMaterial] = useState('');
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [showParticipantWarning, setShowParticipantWarning] = useState(false);
  const [pendingParticipantAction, setPendingParticipantAction] = useState<'add' | 'remove' | 'clear' | null>(null);
  const [pendingParticipantUser, setPendingParticipantUser] = useState<EnhancedUser | null>(null);
  const [isSavingFeedbackMaterial, setIsSavingFeedbackMaterial] = useState(false);
  const [feedbackHoldProgress, setFeedbackHoldProgress] = useState(0);
  const [isHoldingFeedback, setIsHoldingFeedback] = useState(false);
  const [scheduledFeedbackAction, setScheduledFeedbackAction] = useState<CohortAction | null>(null);
  const [isExampleFeedbackEmailExpanded, setIsExampleFeedbackEmailExpanded] = useState(true);
  // Role-keyed preview cache: first load populates cache[mentee|mentor]; toggling
  // between roles is instant on cache hit. Invalidated on cohort change, custom
  // content edits, or custom label edits.
  const [feedbackCache, setFeedbackCache] = useState<{ mentee?: any; mentor?: any }>({});
  const [isLoadingFeedbackPreview, setIsLoadingFeedbackPreview] = useState(false);
  const [isCohortSettingsOpen, setIsCohortSettingsOpen] = useState(false);
  // Introduce & Connect (Double Opt-In) state
  const [isGeneratingConsentEmails, setIsGeneratingConsentEmails] = useState(false);
  const [consentCache, setConsentCache] = useState<{ mentee?: any; mentor?: any }>({});
  const [isLoadingConsentPreview, setIsLoadingConsentPreview] = useState(false);
  const [isConsentEmailExpanded, setIsConsentEmailExpanded] = useState(true);
  const [consentHoldProgress, setConsentHoldProgress] = useState(0);
  const [isHoldingConsent, setIsHoldingConsent] = useState(false);
  const [scheduledConsentAction, setScheduledConsentAction] = useState<CohortAction | null>(null);
  const consentIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [onboardingMaterial, setOnboardingMaterial] = useState('');
  const [isSavingOnboardingMaterial, setIsSavingOnboardingMaterial] = useState(false);
  const [isSendingOnboarding, setIsSendingOnboarding] = useState(false);
  const [onboardingHoldProgress, setOnboardingHoldProgress] = useState(0);
  const [isHoldingOnboarding, setIsHoldingOnboarding] = useState(false);
  const [scheduledOnboardingAction, setScheduledOnboardingAction] = useState<CohortAction | null>(null);
  const [isExampleOnboardingEmailExpanded, setIsExampleOnboardingEmailExpanded] = useState(true);
  const [welcomePackageCache, setWelcomePackageCache] = useState<{ mentee?: any; mentor?: any }>({});
  const [isLoadingWelcomePreview, setIsLoadingWelcomePreview] = useState(false);
  const launchIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onboardingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const feedbackIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Check-in Email state
  const [isSendingCheckIn, setIsSendingCheckIn] = useState(false);
  const [checkInHoldProgress, setCheckInHoldProgress] = useState(0);
  const [isHoldingCheckIn, setIsHoldingCheckIn] = useState(false);
  const [scheduledCheckInAction, setScheduledCheckInAction] = useState<CohortAction | null>(null);
  const [isExampleCheckInEmailExpanded, setIsExampleCheckInEmailExpanded] = useState(true);
  const [checkInCache, setCheckInCache] = useState<{ mentee?: any; mentor?: any }>({});
  const [isLoadingCheckInPreview, setIsLoadingCheckInPreview] = useState(false);
  const [previewRole, setPreviewRole] = useState<'mentee' | 'mentor'>('mentee');

  // Read the currently-visible preview from each cache (falls back to null when
  // the slot has never been populated).
  const welcomePackageEmailExample = welcomePackageCache[previewRole] ?? null;
  const feedbackEmailExample = feedbackCache[previewRole] ?? null;
  const checkInEmailExample = checkInCache[previewRole] ?? null;
  const consentEmailExample = consentCache[previewRole] ?? null;

  const [checkInMaterial, setCheckInMaterial] = useState('');
  const [isSavingCheckInMaterial, setIsSavingCheckInMaterial] = useState(false);
  const checkInIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const unmatchedMenteesRef = useRef<HTMLDivElement | null>(null);
  const unmatchedMentorsRef = useRef<HTMLDivElement | null>(null);
  const prevCohortUsersRef = useRef<string[]>([]);

  // Manual match state
  const [manualMatchMentor, setManualMatchMentor] = useState<EnhancedUser | null>(null);
  const [manualMatchMentee, setManualMatchMentee] = useState<EnhancedUser | null>(null);
  const [manualMatchReasons, setManualMatchReasons] = useState<string[]>([]);
  const [manualMatchReasonInput, setManualMatchReasonInput] = useState('');
  const [isCreatingManualMatch, setIsCreatingManualMatch] = useState(false);
  const [manualMatchMentorSearch, setManualMatchMentorSearch] = useState('');
  const [manualMatchMenteeSearch, setManualMatchMenteeSearch] = useState('');
  const [showConflictInfo, setShowConflictInfo] = useState(false);
  const [conflictingMatches, setConflictingMatches] = useState<MentorshipPair[]>([]);

  // Elapsed timer for match generation
  useEffect(() => {
    if (!isGeneratingMatches || !matchingStartedAt) {
      setMatchingElapsed(0);
      return;
    }
    setMatchingElapsed(Math.floor((Date.now() - matchingStartedAt.getTime()) / 1000));
    const interval = setInterval(() => {
      setMatchingElapsed(Math.floor((Date.now() - matchingStartedAt.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isGeneratingMatches, matchingStartedAt]);

  // Persist cohort selection to localStorage whenever it changes (skip example cohort)
  useEffect(() => {
    if (selectedCohortId && selectedCohortId !== EXAMPLE_COHORT_ID) {
      localStorage.setItem('selectedCohortId', selectedCohortId);
      console.log('💾 Saved cohort selection to localStorage:', selectedCohortId);
    } else {
      localStorage.removeItem('selectedCohortId');
    }
  }, [selectedCohortId]);

  // Reset all cohort-specific state when switching cohorts
  useEffect(() => {
    setPairs([]);
    setUnmatchedMentees([]);
    setUnmatchedMentors([]);
    setRecommendations([]);
    setDistributionMetrics(null);
    setSelectedParticipants([]);
    setPendingParticipants([]);
    setHasLoadedInitialParticipants(false);
    hasLoadedParticipantsRef.current = false;
    prevCohortUsersRef.current = [];
    setExampleEmail(null);
    setFeedbackCache({});
    setConsentCache({});
    setWelcomePackageCache({});
    setCheckInCache({});
    setScheduledSendAction(null);
    setScheduledFeedbackAction(null);
    setScheduledConsentAction(null);
    setScheduledOnboardingAction(null);
    setScheduledCheckInAction(null);
    setOpenStepPopup(null);
  }, [selectedCohortId]);

  // Custom labels state with local edits
  const [customLabels, setCustomLabels] = useState<CustomLabels>({
    mentee: 'Mentee',
    mentor: 'Mentor',
    mentorship: 'Mentorship'
  });

  // Cohort title and description state
  const [cohortTitle, setCohortTitle] = useState('');
  const [cohortDescription, setCohortDescription] = useState('');
  const [maxMenteesPerMentor, setMaxMenteesPerMentor] = useState(5);

  // Helper function to get label with fallback to default
  const getLabel = (key: keyof CustomLabels, defaultValue: string): string => {
    return customLabels[key] || defaultValue;
  };

  // Derive the lock state for the currently selected cohort. Participant and
  // match edits are disabled whenever any email-sending action is scheduled
  // (`active`) or sent (`completed`). Cancelling a scheduled action flips its
  // status to `terminated`, which re-enables edits automatically.
  const selectedCohort = useMemo(
    () => cohorts.find((c) => c.id === selectedCohortId) || null,
    [cohorts, selectedCohortId],
  );
  const cohortLockState = useMemo(() => getCohortLockState(selectedCohort), [selectedCohort]);
  const isCohortLocked = cohortLockState.locked;
  const lockTooltip = useMemo(() => {
    if (!isCohortLocked) return '';
    if (cohortLockState.reason === 'sent') {
      return 'Locked - emails have already been sent for this cohort.';
    }
    const stepNames = cohortLockState.triggeringActions.map((a) => getActionStepLabel(a.action));
    if (stepNames.length <= 1) {
      return `Locked - cancel the scheduled send in the ${stepNames[0] ?? 'scheduled'} step to edit.`;
    }
    const last = stepNames[stepNames.length - 1];
    const head = stepNames.slice(0, -1).join(', ');
    return `Locked - cancel all scheduled sends (${head} and ${last}) to edit.`;
  }, [isCohortLocked, cohortLockState]);

  // Invalidate role-keyed preview caches when the cohort's custom labels change.
  // Mentor/mentee/mentorship labels appear in every email type's preview, so any
  // edit makes cached previews stale.
  useEffect(() => {
    setWelcomePackageCache({});
    setFeedbackCache({});
    setCheckInCache({});
    setConsentCache({});
  }, [
    selectedCohort?.customLabels?.mentor,
    selectedCohort?.customLabels?.mentee,
    selectedCohort?.customLabels?.mentorship,
  ]);

  // Helper functions for participant changes with warning when matches exist
  const handleAddParticipant = (user: EnhancedUser) => {
    if (selectedCohortId === EXAMPLE_COHORT_ID) return;
    if (isCohortLocked) return;
    if (pairs.length > 0) {
      setPendingParticipantAction('add');
      setPendingParticipantUser(user);
      setShowParticipantWarning(true);
    } else {
      setPendingParticipants([...pendingParticipants, user]);
    }
  };

  const handleRemoveParticipant = (user: EnhancedUser) => {
    if (selectedCohortId === EXAMPLE_COHORT_ID) return;
    if (isCohortLocked) return;
    if (pairs.length > 0) {
      setPendingParticipantAction('remove');
      setPendingParticipantUser(user);
      setShowParticipantWarning(true);
    } else {
      setPendingParticipants(pendingParticipants.filter(p => p.uid !== user.uid));
    }
  };

  const handleSelectAllParticipants = () => {
    if (selectedCohortId === EXAMPLE_COHORT_ID) return;
    if (isCohortLocked) return;
    if (pairs.length > 0) {
      setPendingParticipantAction('add');
      setPendingParticipantUser(null);
      setShowParticipantWarning(true);
    } else {
      setPendingParticipants(allUsers);
      notificationService.success('All users selected');
    }
  };

  const handleClearAllParticipants = () => {
    if (selectedCohortId === EXAMPLE_COHORT_ID) return;
    if (isCohortLocked) return;
    if (pairs.length > 0) {
      setPendingParticipantAction('clear');
      setPendingParticipantUser(null);
      setShowParticipantWarning(true);
    } else {
      setPendingParticipants([]);
      setParticipantSearch('');
      setOpenStepPopup(null);
    }
  };

  // Handle creating a manual match - checks for existing matches first
  const handleCreateManualMatch = async () => {
    if (!manualMatchMentor || !manualMatchMentee || !currentTenantId || !selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID) {
      notificationService.error('Please select both a mentor and a mentee');
      return;
    }
    if (isCohortLocked) {
      notificationService.warning(lockTooltip);
      return;
    }
    if (manualMatchReasons.length === 0) {
      notificationService.error('Please add at least one match reason');
      return;
    }

    // Check for exact duplicate match - only this blocks creation
    const exactDuplicate = pairs.find(
      pair => pair.mentor.uid === manualMatchMentor.uid && pair.mentee.uid === manualMatchMentee.uid
    );
    if (exactDuplicate) {
      notificationService.error('This exact match already exists');
      return;
    }

    // Check if the selected mentor or mentee already has a match in this cohort
    const mentorMatches = pairs.filter(p => p.mentor.uid === manualMatchMentor.uid);
    const menteeMatches = pairs.filter(p => p.mentee.uid === manualMatchMentee.uid);
    const relevantMatches = [...mentorMatches, ...menteeMatches];

    if (relevantMatches.length > 0) {
      setConflictingMatches(relevantMatches);
      setShowConflictInfo(true);
    } else {
      // No conflicts, create directly
      await executeCreateManualMatch();
    }
  };

  // Execute the manual match creation
  const executeCreateManualMatch = async () => {
    if (!manualMatchMentor || !manualMatchMentee || !currentTenantId || !selectedCohortId) return;

    setIsCreatingManualMatch(true);

    try {
      const matchId = `${manualMatchMentee.uid}_${manualMatchMentor.uid}`;
      const matchRef = doc(db, `tenants/${currentTenantId}/cohorts/${selectedCohortId}/matches`, matchId);

      const reasons = manualMatchReasons;

      await setDoc(matchRef, {
        menteeId: manualMatchMentee.uid,
        mentorId: manualMatchMentor.uid,
        createdAt: serverTimestamp(),
        matchedAt: serverTimestamp(),
        matchMetrics: reasons,
        matchScore: {
          compatibility_score: 1.0,
          compatibility_breakdown: {
            professional: 1.0,
            interests: 1.0,
            education: 1.0,
            overall: 1.0
          }
        }
      });

      // Add the new pair to the local state
      const newPair: MentorshipPair = {
        id: matchId,
        mentee: {
          uid: manualMatchMentee.uid,
          firstName: manualMatchMentee.profile?.firstName || manualMatchMentee.displayName.split(' ')[0] || '',
          lastName: manualMatchMentee.profile?.lastName || manualMatchMentee.displayName.split(' ').slice(1).join(' ') || '',
          email: manualMatchMentee.profile?.email || manualMatchMentee.authEmail || '',
          role: manualMatchMentee.profile?.works?.[0]?.position || 'Mentee'
        },
        mentor: {
          uid: manualMatchMentor.uid,
          firstName: manualMatchMentor.profile?.firstName || manualMatchMentor.displayName.split(' ')[0] || '',
          lastName: manualMatchMentor.profile?.lastName || manualMatchMentor.displayName.split(' ').slice(1).join(' ') || '',
          email: manualMatchMentor.profile?.email || manualMatchMentor.authEmail || '',
          role: manualMatchMentor.profile?.works?.[0]?.position || 'Mentor'
        },
        matchReason: reasons.join(', '),
        status: 'pending',
        matchMetrics: reasons,
        matchScore: {
          compatibility_score: 1.0,
          compatibility_breakdown: {
            professional: 1.0,
            interests: 1.0,
            education: 1.0,
            overall: 1.0
          }
        }
      };

      setPairs(prev => [...prev, newPair]);

      // Close popup and reset state
      setOpenStepPopup(null);
      setManualMatchMentor(null);
      setManualMatchMentee(null);
      setManualMatchReasons([]);
      setManualMatchReasonInput('');
      setShowConflictInfo(false);
      setConflictingMatches([]);

      notificationService.success('Match created successfully');
      console.log('✅ Manual match created:', matchId);
    } catch (error) {
      console.error('❌ Error creating manual match:', error);
      notificationService.error('Failed to create match. Please try again.');
    } finally {
      setIsCreatingManualMatch(false);
    }
  };

  // Handle removing a match
  const handleRemoveMatch = (pair: MentorshipPair) => {
    if (selectedCohortId === EXAMPLE_COHORT_ID) {
      notificationService.warning('Cannot remove matches from the example cohort');
      return;
    }
    if (isCohortLocked) {
      notificationService.warning(lockTooltip);
      return;
    }

    setConfirmConfig({
      isOpen: true,
      title: 'Remove Match',
      message: `Are you sure you want to remove the match between ${pair.mentor.firstName} ${pair.mentor.lastName} and ${pair.mentee.firstName} ${pair.mentee.lastName}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          if (!currentTenantId || !selectedCohortId) {
            notificationService.error('No cohort selected');
            return;
          }

          // Delete from Firestore
          const matchRef = doc(db, `tenants/${currentTenantId}/cohorts/${selectedCohortId}/matches/${pair.id}`);
          await deleteDoc(matchRef);

          // Update local state
          setPairs(prev => prev.filter(p => p.id !== pair.id));

          notificationService.success('Match removed successfully');
          console.log('✅ Match removed:', pair.id);
        } catch (error) {
          console.error('❌ Error removing match:', error);
          notificationService.error('Failed to remove match. Please try again.');
        } finally {
          setConfirmConfig(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const confirmParticipantChange = async () => {
    // Clear existing matches from Firestore
    if (currentTenantId && selectedCohortId && selectedCohortId !== EXAMPLE_COHORT_ID) {
      try {
        const matchesRef = collection(db, `tenants/${currentTenantId}/cohorts/${selectedCohortId}/matches`);
        const matchesSnapshot = await getDocs(matchesRef);

        if (matchesSnapshot.docs.length > 0) {
          const deletePromises = matchesSnapshot.docs.map(matchDoc =>
            deleteDoc(matchDoc.ref)
          );
          await Promise.all(deletePromises);
          console.log(`✅ Deleted ${matchesSnapshot.docs.length} matches from Firestore`);
        }
      } catch (error) {
        console.error('Error clearing matches from Firestore:', error);
      }
    }

    // Clear existing matches and unmatched participants in UI
    setPairs([]);
    setUnmatchedMentees([]);
    setUnmatchedMentors([]);
    setRecommendations([]);
    setDistributionMetrics(null);

    if (pendingParticipantAction === 'add' && pendingParticipantUser) {
      setPendingParticipants([...pendingParticipants, pendingParticipantUser]);
    } else if (pendingParticipantAction === 'add' && !pendingParticipantUser) {
      setPendingParticipants(allUsers);
      notificationService.success('All users selected');
    } else if (pendingParticipantAction === 'remove' && pendingParticipantUser) {
      setPendingParticipants(pendingParticipants.filter(p => p.uid !== pendingParticipantUser.uid));
    } else if (pendingParticipantAction === 'clear') {
      setPendingParticipants([]);
      setParticipantSearch('');
      setOpenStepPopup(null);
    }
    setShowParticipantWarning(false);
    setPendingParticipantAction(null);
    setPendingParticipantUser(null);

    notificationService.info('Matches cleared. Please regenerate matches with the updated participants.');
  };

  const cancelParticipantChange = () => {
    setShowParticipantWarning(false);
    setPendingParticipantAction(null);
    setPendingParticipantUser(null);
  };

  // Load customLabels, title, and description from selected cohort when it changes
  useEffect(() => {
    const selectedCohort = cohorts.find(c => c.id === selectedCohortId);
    if (selectedCohort) {
      // Load custom labels
      if (selectedCohort.customLabels) {
        setCustomLabels({
          mentee: selectedCohort.customLabels.mentee || 'Mentee',
          mentor: selectedCohort.customLabels.mentor || 'Mentor',
          mentorship: selectedCohort.customLabels.mentorship || 'Mentorship'
        });
      } else {
        setCustomLabels({
          mentee: 'Mentee',
          mentor: 'Mentor',
          mentorship: 'Mentorship'
        });
      }

      // Load title and description
      setCohortTitle(selectedCohort.title || '');
      setCohortDescription(selectedCohort.description || '');
      setMaxMenteesPerMentor((selectedCohort as any).customMentorMenteeRatio || 5);
    } else {
      // Reset to defaults if no cohort selected
      setCustomLabels({
        mentee: 'Mentee',
        mentor: 'Mentor',
        mentorship: 'Mentorship'
      });
      setCohortTitle('');
      setCohortDescription('');
      setMaxMenteesPerMentor(5);
    }
  }, [selectedCohortId, cohorts]);

  // Fetch available cohorts when tenant changes
  useEffect(() => {
    const fetchCohorts = async () => {
      if (!currentTenantId) {
        console.log('ℹ️ No tenant ID available, showing only example cohort');
        setCohorts([EXAMPLE_COHORT]);
        // Only set example cohort if nothing is saved in localStorage
        const savedCohortId = localStorage.getItem('selectedCohortId');
        if (!savedCohortId) {
          setSelectedCohortId(EXAMPLE_COHORT_ID);
        }
        return;
      }

      setIsLoadingCohorts(true);
      try {
        console.log('🔄 Fetching cohorts for tenant:', currentTenantId);
        const cohortsRef = collection(db, 'tenants', currentTenantId, 'cohorts');
        const cohortsQuery = query(cohortsRef, orderBy('createdAt', 'desc'));
        const cohortsSnapshot = await getDocs(cohortsQuery);

        const fetchedCohorts: Cohort[] = [];
        cohortsSnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedCohorts.push({
            id: doc.id,
            title: data.title || `Cohort ${doc.id}`,
            description: data.description,
            createdAt: data.createdAt?.toDate() || new Date(),
            status: data.status || 'draft',
            users: data.users || [],
            actions: data.actions || [],
            customLabels: data.customLabels,
            workflowType: data.workflowType || 'standard',
            presetActions: data.presetActions || null
          });
        });

        // Add example cohort at the beginning
        const allCohorts = [EXAMPLE_COHORT, ...fetchedCohorts];

        setCohorts(allCohorts);
        console.log(`✅ Loaded ${fetchedCohorts.length} cohorts (+ 1 example cohort)`);

        // Handle cohort selection logic
        if (allCohorts.length > 0) {
          // Check if previously selected cohort still exists
          const savedCohortId = localStorage.getItem('selectedCohortId');
          const savedCohortExists = savedCohortId && allCohorts.some(c => c.id === savedCohortId);

          if (savedCohortExists) {
            // Restore the saved selection if it still exists
            setSelectedCohortId(savedCohortId);
            console.log(`✅ Restored saved cohort selection: ${savedCohortId}`);
          } else if (!selectedCohortId) {
            // If no cohort was selected, auto-select the example cohort by default
            setSelectedCohortId(EXAMPLE_COHORT_ID);
            console.log(`✅ Auto-selected example cohort for first-time users`);
          }
        } else {
          // No cohorts available (only example)
          setSelectedCohortId(EXAMPLE_COHORT_ID);
        }
      } catch (error) {
        console.error('❌ Error fetching cohorts:', error);
        notificationService.error('Failed to load cohorts');
        // Still show example cohort even on error
        setCohorts([EXAMPLE_COHORT]);
        setSelectedCohortId(EXAMPLE_COHORT_ID);
      } finally {
        setIsLoadingCohorts(false);
      }
    };

    fetchCohorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  // Fetch users and load existing participants when Select Participants popup opens
  useEffect(() => {
    const fetchUsers = async () => {
      if (openStepPopup === 'selectParticipants' && currentTenantId) {
        setLoadingUsers(true);
        setHasLoadedInitialParticipants(false);
        hasLoadedParticipantsRef.current = false;
        try {
          // Fetch all users for tenant
          const users = await userDataService.fetchUsersForTenant(currentTenantId);
          setAllUsers(users);

          // Load existing participants from cohort document
          if (selectedCohortId && selectedCohortId !== EXAMPLE_COHORT_ID) {
            const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
            const cohortDoc = await getDoc(cohortRef);

            if (cohortDoc.exists()) {
              const cohortData = cohortDoc.data();
              const userIds = cohortData.users || [];

              // Filter users to get only selected participants
              const selectedUsers = users.filter(user => userIds.includes(user.uid));
              setSelectedParticipants(selectedUsers);
              setPendingParticipants(selectedUsers); // Initialize pending state
            } else {
              setSelectedParticipants([]);
              setPendingParticipants([]);
            }
          } else {
            setSelectedParticipants([]);
            setPendingParticipants([]);
          }

          // Mark that we've finished loading initial participants
          setHasLoadedInitialParticipants(true);
          hasLoadedParticipantsRef.current = true;
        } catch (error) {
          console.error('Error fetching users:', error);
          notificationService.error('Failed to load users');
          setHasLoadedInitialParticipants(true);
          hasLoadedParticipantsRef.current = true;
        } finally {
          setLoadingUsers(false);
        }
      } else if (openStepPopup !== 'selectParticipants') {
        // Reset state when popup closes (so re-opening will re-fetch)
        // Note: only reset the state, NOT the ref — the ref guards the save effect
        // and must stay true so pending saves from this render cycle can complete
        setHasLoadedInitialParticipants(false);
      }
    };

    fetchUsers();
  }, [openStepPopup, currentTenantId, selectedCohortId]);

  // Load participants when Create Matches or Manual Match popup opens (if not already loaded)
  useEffect(() => {
    const loadParticipantsForMatching = async () => {
      if ((openStepPopup === 'createMatches' || openStepPopup === 'manualMatch') && currentTenantId && selectedCohortId && selectedCohortId !== EXAMPLE_COHORT_ID) {
        try {
          // Always load all users if not already loaded
          let users = allUsers;
          if (allUsers.length === 0) {
            users = await userDataService.fetchUsersForTenant(currentTenantId);
            setAllUsers(users);
            console.log('✅ Loaded all users:', users.length);
          }

          // If participants are not loaded yet, load them from cohort
          if (selectedParticipants.length === 0) {
            const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
            const cohortDoc = await getDoc(cohortRef);

            if (cohortDoc.exists()) {
              const cohortData = cohortDoc.data();
              const userIds = cohortData.users || [];

              if (userIds.length > 0) {
                const selectedUsers = users.filter(user => userIds.includes(user.uid));
                setSelectedParticipants(selectedUsers);
                console.log('✅ Loaded participants for matching:', selectedUsers.length);
              }
            }
          }
        } catch (error) {
          console.error('Error loading participants for matching:', error);
        }
      }
    };

    loadParticipantsForMatching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openStepPopup, currentTenantId, selectedCohortId]);

  // Load onboarding material when Onboarding popup opens
  useEffect(() => {
    const loadOnboardingData = async () => {
      if (openStepPopup === 'onboarding' && selectedCohortId) {
        // Handle Example Cohort
        if (selectedCohortId === EXAMPLE_COHORT_ID) {
          const exampleCohort = cohorts.find(c => c.id === EXAMPLE_COHORT_ID);
          if (exampleCohort) {
            const onboardingAction = exampleCohort.actions.find(
              (action: any) => action.action === 'welcomePackage'
            );

            if (onboardingAction) {
              const formattedAction: CohortAction = {
                ...onboardingAction,
                scheduledAt: onboardingAction.metadata?.scheduledAt
                  ? new Date(onboardingAction.metadata.scheduledAt)
                  : undefined,
                createdAt: onboardingAction.createdAt instanceof Date
                  ? onboardingAction.createdAt
                  : new Date(onboardingAction.createdAt),
                completedAt: onboardingAction.completedAt instanceof Date
                  ? onboardingAction.completedAt
                  : onboardingAction.completedAt ? new Date(onboardingAction.completedAt) : undefined
              };
              setScheduledOnboardingAction(formattedAction);
              setOnboardingMaterial('Welcome to the program! Please review these resources: https://docs.google.com/presentation/xyz');
              console.log('Found onboarding action in example cohort:', formattedAction);
            } else {
              setScheduledOnboardingAction(null);
            }
            // Example cohort has static samples for both roles — pre-fill both so
            // toggling between Mentee and Mentor is instant.
            if (welcomePackageCache.mentee === undefined && welcomePackageCache.mentor === undefined) {
              setWelcomePackageCache({
                mentee: exampleCohort.welcomePackageEmailExample || null,
                mentor: exampleCohort.welcomePackageEmailExampleMentor || exampleCohort.welcomePackageEmailExample || null,
              });
            }
          }
          return;
        }

        // Handle real cohorts
        if (currentTenantId) {
          try {
            const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
            const cohortDoc = await getDoc(cohortRef);

            if (cohortDoc.exists()) {
              const cohortData = cohortDoc.data();

              // Load onboarding material
              setOnboardingMaterial(cohortData.customWelcomePackageContent || '');

              // Find the most recent onboarding action (active or completed)
              const actions = cohortData.actions || [];
              const latestOnboardingAction = actions
                .filter((action: any) => action.action === 'welcomePackage' && (action.status === 'active' || action.status === 'completed'))
                .sort((a: any, b: any) => (b.createdAt?.toDate?.()?.getTime() || 0) - (a.createdAt?.toDate?.()?.getTime() || 0))[0];

              if (latestOnboardingAction) {
                const onboardingAction: CohortAction = {
                  ...latestOnboardingAction,
                  scheduledAt: latestOnboardingAction.scheduledAt?.toDate?.() || latestOnboardingAction.scheduledAt,
                  createdAt: latestOnboardingAction.createdAt?.toDate?.() || latestOnboardingAction.createdAt,
                  completedAt: latestOnboardingAction.completedAt?.toDate?.() || latestOnboardingAction.completedAt
                };
                setScheduledOnboardingAction(onboardingAction);
              } else {
                setScheduledOnboardingAction(null);
              }
            }

            // `undefined` = never fetched, `null` = fetched and the API returned no
            // sample. Both states must short-circuit, otherwise a null cache slot
            // refires the effect (cache is in deps) and the fetch loops forever.
            if (welcomePackageCache[previewRole] !== undefined) {
              return;
            }

            // Fetch example welcome package email using preview endpoint
            setIsLoadingWelcomePreview(true);
            try {
              const previewResult = await generateEmails({
                tenant_id: currentTenantId,
                cohort_id: selectedCohortId,
                email_type: 'welcomePackage',
                preview: true,
                preview_role: previewRole
              });

              const freshSample = previewResult.success && previewResult.data?.sample
                ? previewResult.data.sample
                : null;
              setWelcomePackageCache((prev) => ({ ...prev, [previewRole]: freshSample }));
              console.log(freshSample
                ? '✅ Loaded welcome package email from preview endpoint'
                : 'ℹ️ No preview welcome package email available');
            } finally {
              setIsLoadingWelcomePreview(false);
            }
          } catch (error) {
            console.error('Error loading onboarding data:', error);
            setIsLoadingWelcomePreview(false);
          }
        }
      } else {
        // Clear action + material on popup close, but keep the preview cache
        // so reopening the popup for the same cohort is instant.
        setScheduledOnboardingAction(null);
        setOnboardingMaterial('');
      }
    };

    loadOnboardingData();
  }, [openStepPopup, currentTenantId, selectedCohortId, cohorts, previewRole, welcomePackageCache]);

  // Load feedback material when Collect Feedback popup opens
  useEffect(() => {
    const loadFeedbackData = async () => {
      if (openStepPopup === 'collectFeedback' && selectedCohortId) {
        // Handle Example Cohort
        if (selectedCohortId === EXAMPLE_COHORT_ID) {
          const exampleCohort = cohorts.find(c => c.id === EXAMPLE_COHORT_ID);
          if (exampleCohort) {
            const activeFeedbackAction = exampleCohort.actions.find(
              (action: any) => action.action === 'collectFeedback' && action.status === 'active'
            );

            if (activeFeedbackAction) {
              const feedbackAction: CohortAction = {
                ...activeFeedbackAction,
                scheduledAt: activeFeedbackAction.metadata?.scheduledAt
                  ? new Date(activeFeedbackAction.metadata.scheduledAt)
                  : undefined,
                createdAt: activeFeedbackAction.createdAt instanceof Date
                  ? activeFeedbackAction.createdAt
                  : new Date(activeFeedbackAction.createdAt),
                completedAt: activeFeedbackAction.completedAt instanceof Date
                  ? activeFeedbackAction.completedAt
                  : activeFeedbackAction.completedAt ? new Date(activeFeedbackAction.completedAt) : undefined
              };
              setScheduledFeedbackAction(feedbackAction);
              setFeedbackMaterial('Please complete our mentorship feedback survey: https://forms.google.com/mentorship-feedback');
              // Pre-fill both roles from the example cohort's static samples so
              // toggling between Mentee and Mentor is instant.
              if (feedbackCache.mentee === undefined && feedbackCache.mentor === undefined) {
                setFeedbackCache({
                  mentee: exampleCohort.feedbackEmailExample || null,
                  mentor: exampleCohort.feedbackEmailExampleMentor || exampleCohort.feedbackEmailExample || null,
                });
              }
              console.log('📧 Found active scheduled feedback send in example cohort:', feedbackAction);
            } else {
              setScheduledFeedbackAction(null);
              setFeedbackCache({});
            }
          }
          return;
        }

        // Handle real cohorts
        if (currentTenantId) {
          try {
            const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
            const cohortDoc = await getDoc(cohortRef);

            if (cohortDoc.exists()) {
              const cohortData = cohortDoc.data();

              // Load feedback material
              setFeedbackMaterial(cohortData.customFeedbackContent || '');

              // Find the most recent feedback action (active or completed)
              const actions = cohortData.actions || [];
              const latestFeedbackAction = actions
                .filter((action: any) => action.action === 'collectFeedback' && (action.status === 'active' || action.status === 'completed'))
                .sort((a: any, b: any) => (b.createdAt?.toDate?.()?.getTime() || 0) - (a.createdAt?.toDate?.()?.getTime() || 0))[0];

              if (latestFeedbackAction) {
                const feedbackAction: CohortAction = {
                  ...latestFeedbackAction,
                  scheduledAt: latestFeedbackAction.scheduledAt?.toDate?.() || latestFeedbackAction.scheduledAt,
                  createdAt: latestFeedbackAction.createdAt?.toDate?.() || latestFeedbackAction.createdAt,
                  completedAt: latestFeedbackAction.completedAt?.toDate?.() || latestFeedbackAction.completedAt
                };
                setScheduledFeedbackAction(feedbackAction);
              } else {
                setScheduledFeedbackAction(null);
              }
            }

            // `undefined` = never fetched, `null` = fetched and the API returned no
            // sample. Both states must short-circuit, otherwise a null cache slot
            // refires the effect (cache is in deps) and the fetch loops forever.
            if (feedbackCache[previewRole] !== undefined) {
              return;
            }

            // Fetch example feedback email using preview endpoint
            setIsLoadingFeedbackPreview(true);
            try {
              const previewResult = await generateEmails({
                tenant_id: currentTenantId,
                cohort_id: selectedCohortId,
                email_type: 'feedback',
                preview: true,
                preview_role: previewRole
              });

              const freshSample = previewResult.success && previewResult.data?.sample
                ? previewResult.data.sample
                : null;
              setFeedbackCache((prev) => ({ ...prev, [previewRole]: freshSample }));
              console.log(freshSample
                ? '✅ Loaded feedback email from preview endpoint'
                : 'ℹ️ No preview feedback email available');
            } finally {
              setIsLoadingFeedbackPreview(false);
            }
          } catch (error) {
            console.error('Error loading feedback data:', error);
            setIsLoadingFeedbackPreview(false);
          }
        }
      } else {
        // Clear action + material on popup close, but keep the preview cache
        // so reopening the popup for the same cohort is instant.
        setScheduledFeedbackAction(null);
        setFeedbackMaterial('');
      }
    };

    loadFeedbackData();
  }, [openStepPopup, currentTenantId, selectedCohortId, cohorts, previewRole, feedbackCache]);

  useEffect(() => {
    const loadCheckInData = async () => {
      if (openStepPopup === 'checkIn' && selectedCohortId) {
        if (selectedCohortId === EXAMPLE_COHORT_ID) {
          const exampleCohort = cohorts.find(c => c.id === EXAMPLE_COHORT_ID);
          if (exampleCohort) {
            const checkInAction = exampleCohort.actions.find(
              (action: any) => action.action === 'sendCheckIn'
            );

            if (checkInAction) {
              const formattedAction: CohortAction = {
                ...checkInAction,
                scheduledAt: checkInAction.metadata?.scheduledAt
                  ? new Date(checkInAction.metadata.scheduledAt)
                  : undefined,
                createdAt: checkInAction.createdAt instanceof Date
                  ? checkInAction.createdAt
                  : new Date(checkInAction.createdAt),
                completedAt: checkInAction.completedAt instanceof Date
                  ? checkInAction.completedAt
                  : checkInAction.completedAt ? new Date(checkInAction.completedAt) : undefined
              };
              setScheduledCheckInAction(formattedAction);
              console.log('Found check-in action in example cohort:', formattedAction);
            } else {
              setScheduledCheckInAction(null);
            }
            // Pre-fill both roles from the example cohort's static samples so
            // toggling between Mentee and Mentor is instant.
            if (checkInCache.mentee === undefined && checkInCache.mentor === undefined) {
              setCheckInCache({
                mentee: exampleCohort.checkInEmailExample || null,
                mentor: exampleCohort.checkInEmailExampleMentor || exampleCohort.checkInEmailExample || null,
              });
            }
          }
          return;
        }

        if (currentTenantId) {
          try {
            const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
            const cohortDoc = await getDoc(cohortRef);

            if (cohortDoc.exists()) {
              const cohortData = cohortDoc.data();

              // Load check-in material
              setCheckInMaterial(cohortData.customCheckInContent || '');

              // Find the most recent check-in action (active or completed)
              const actions = cohortData.actions || [];
              const latestCheckInAction = actions
                .filter((action: any) => action.action === 'sendCheckIn' && (action.status === 'active' || action.status === 'completed'))
                .sort((a: any, b: any) => (b.createdAt?.toDate?.()?.getTime() || 0) - (a.createdAt?.toDate?.()?.getTime() || 0))[0];

              if (latestCheckInAction) {
                const checkInAction: CohortAction = {
                  ...latestCheckInAction,
                  scheduledAt: latestCheckInAction.scheduledAt?.toDate?.() || latestCheckInAction.scheduledAt,
                  createdAt: latestCheckInAction.createdAt?.toDate?.() || latestCheckInAction.createdAt,
                  completedAt: latestCheckInAction.completedAt?.toDate?.() || latestCheckInAction.completedAt
                };
                setScheduledCheckInAction(checkInAction);
              } else {
                setScheduledCheckInAction(null);
              }
            }

            // `undefined` = never fetched, `null` = fetched and the API returned no
            // sample. Both states must short-circuit, otherwise a null cache slot
            // refires the effect (cache is in deps) and the fetch loops forever.
            if (checkInCache[previewRole] !== undefined) {
              return;
            }

            // Fetch example check-in email using preview endpoint
            setIsLoadingCheckInPreview(true);
            try {
              const previewResult = await generateEmails({
                tenant_id: currentTenantId,
                cohort_id: selectedCohortId,
                email_type: 'checkIn',
                preview: true,
                preview_role: previewRole
              });

              const freshSample = previewResult.success && previewResult.data?.sample
                ? previewResult.data.sample
                : null;
              setCheckInCache((prev) => ({ ...prev, [previewRole]: freshSample }));
              console.log(freshSample
                ? '✅ Loaded check-in email from preview endpoint'
                : 'ℹ️ No preview check-in email available');
            } finally {
              setIsLoadingCheckInPreview(false);
            }
          } catch (error) {
            console.error('Error loading check-in data:', error);
            setIsLoadingCheckInPreview(false);
          }
        }
      } else if (openStepPopup !== 'checkIn') {
        // Clear action + material on popup close, but keep the preview cache
        // so reopening the popup for the same cohort is instant.
        setScheduledCheckInAction(null);
        setCheckInMaterial('');
      }
    };

    loadCheckInData();
  }, [openStepPopup, currentTenantId, selectedCohortId, cohorts, previewRole, checkInCache]);

  // Load example email and check for scheduled send when Launch popup opens
  useEffect(() => {
    const loadLaunchData = async () => {
      if (openStepPopup === 'launch' && selectedCohortId) {
        // Handle Example Cohort
        if (selectedCohortId === EXAMPLE_COHORT_ID) {
          const exampleCohort = cohorts.find(c => c.id === EXAMPLE_COHORT_ID);
          if (exampleCohort && exampleCohort.connectionEmailExample) {
            setExampleEmail(exampleCohort.connectionEmailExample);
            console.log('✅ Loaded example connection email from example cohort');
          }
          return;
        }

        // Handle real cohorts
        if (currentTenantId) {
          try {
            const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
            const cohortDoc = await getDoc(cohortRef);

            if (cohortDoc.exists()) {
              const cohortData = cohortDoc.data();

              // Find the most recent sendMatches action (active or completed)
              const actions = cohortData.actions || [];
              const latestSendAction = actions
                .filter((action: any) => action.action === 'sendMatches' && (action.status === 'active' || action.status === 'completed'))
                .sort((a: any, b: any) => (b.createdAt?.toDate?.()?.getTime() || 0) - (a.createdAt?.toDate?.()?.getTime() || 0))[0];

              if (latestSendAction) {
                const sendAction: CohortAction = {
                  ...latestSendAction,
                  scheduledAt: latestSendAction.scheduledAt?.toDate?.() || latestSendAction.scheduledAt,
                  createdAt: latestSendAction.createdAt?.toDate?.() || latestSendAction.createdAt,
                  completedAt: latestSendAction.completedAt?.toDate?.() || latestSendAction.completedAt
                };
                setScheduledSendAction(sendAction);
              } else {
                setScheduledSendAction(null);
              }
            }

            // Fetch example email using preview endpoint
            setIsLoadingConnectionPreview(true);
            try {
              const previewResult = await generateEmails({
                tenant_id: currentTenantId,
                cohort_id: selectedCohortId,
                email_type: 'connection',
                preview: true
              });

              if (previewResult.success && previewResult.data?.sample) {
                setExampleEmail(previewResult.data.sample);
                console.log('✅ Loaded example email from preview endpoint');
              } else {
                setExampleEmail(null);
                console.log('ℹ️ No preview email available');
              }
            } finally {
              setIsLoadingConnectionPreview(false);
            }
          } catch (error) {
            console.error('Error loading launch data:', error);
            setIsLoadingConnectionPreview(false);
          }
        }
      } else {
        // Clear when popup closes
        setScheduledSendAction(null);
        setExampleEmail(null);
      }
    };

    loadLaunchData();
  }, [openStepPopup, currentTenantId, selectedCohortId, cohorts]);

  // Load consent email preview when Introduce & Connect popup opens
  useEffect(() => {
    const loadConsentData = async () => {
      if (openStepPopup === 'introduceAndConnect' && selectedCohortId) {
        // Handle Example Cohort
        if (selectedCohortId === EXAMPLE_COHORT_ID) {
          return;
        }

        // Handle real cohorts
        if (currentTenantId) {
          try {
            const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
            const cohortDoc = await getDoc(cohortRef);

            if (cohortDoc.exists()) {
              const cohortData = cohortDoc.data();

              // Find the most recent consent action (active or completed)
              const actions = cohortData.actions || [];
              const latestConsentAction = actions
                .filter((action: any) => action.action === 'sendIntroConsent' && (action.status === 'active' || action.status === 'completed'))
                .sort((a: any, b: any) => (b.createdAt?.toDate?.()?.getTime() || 0) - (a.createdAt?.toDate?.()?.getTime() || 0))[0];

              if (latestConsentAction) {
                const consentAction: CohortAction = {
                  ...latestConsentAction,
                  scheduledAt: latestConsentAction.scheduledAt?.toDate?.() || latestConsentAction.scheduledAt,
                  createdAt: latestConsentAction.createdAt?.toDate?.() || latestConsentAction.createdAt,
                  completedAt: latestConsentAction.completedAt?.toDate?.() || latestConsentAction.completedAt
                };
                setScheduledConsentAction(consentAction);
              } else {
                setScheduledConsentAction(null);
              }
            }

            // `undefined` = never fetched, `null` = fetched and the API returned no
            // sample. Both states must short-circuit, otherwise a null cache slot
            // refires the effect (cache is in deps) and the fetch loops forever.
            if (consentCache[previewRole] !== undefined) {
              return;
            }

            // Fetch example consent email using preview endpoint
            setIsLoadingConsentPreview(true);
            try {
              const previewResult = await generateEmails({
                tenant_id: currentTenantId,
                cohort_id: selectedCohortId,
                email_type: 'introConsent',
                preview: true,
                preview_role: previewRole
              });

              const freshSample = previewResult.success && previewResult.data?.sample
                ? previewResult.data.sample
                : null;
              setConsentCache((prev) => ({ ...prev, [previewRole]: freshSample }));
              console.log(freshSample
                ? '✅ Loaded consent email from preview endpoint'
                : 'ℹ️ No preview consent email available');
            } finally {
              setIsLoadingConsentPreview(false);
            }
          } catch (error) {
            console.error('Error loading consent data:', error);
            setIsLoadingConsentPreview(false);
          }
        }
      } else if (openStepPopup !== 'introduceAndConnect') {
        // Clear action on popup close, but keep the preview cache
        // so reopening the popup for the same cohort is instant.
        setScheduledConsentAction(null);
      }
    };

    loadConsentData();
  }, [openStepPopup, currentTenantId, selectedCohortId, previewRole, consentCache]);

  // Save selected participants to cohort document in real-time
  useEffect(() => {
    const saveParticipants = async () => {
      if (!selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId || !hasLoadedParticipantsRef.current) {
        return;
      }

      try {
        const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
        const userIds = selectedParticipants.map(user => user.uid);

        // Get current cohort data to update actions and presetActions
        const cohortDoc = await getDoc(cohortRef);
        let actions: CohortAction[] = [];
        let presetActions: PresetAction[] = [];
        let existingUserIds: string[] = [];

        if (cohortDoc.exists()) {
          actions = cohortDoc.data().actions || [];
          presetActions = cohortDoc.data().presetActions || [];
          existingUserIds = cohortDoc.data().users || [];
        }

        // PROTECTION: Don't save empty array if cohort already has users
        // This prevents accidental clearing of users due to race conditions or load failures
        if (userIds.length === 0 && existingUserIds.length > 0) {
          console.log('⚠️ Skipping save: would clear existing users. This is likely a bug.');
          return;
        }

        // Find existing selectParticipants action or create new one
        const selectParticipantsIndex = actions.findIndex(a => a.action === 'selectParticipants');
        const now = new Date();

        if (selectParticipantsIndex >= 0) {
          // Update existing action
          actions[selectParticipantsIndex] = {
            ...actions[selectParticipantsIndex],
            status: 'completed',
            completedAt: now
          };
        } else {
          // Create new action
          actions.push({
            action: 'selectParticipants',
            status: 'completed',
            createdAt: now,
            completedAt: now
          });
        }

        // Also update presetActions status if it exists
        const updatedPresetActions = presetActions.map(preset =>
          preset.key === 'selectParticipants'
            ? { ...preset, status: 'completed' as const, completedAt: now }
            : preset
        );

        // Save users, actions, and presetActions
        await updateDoc(cohortRef, {
          users: userIds,
          actions: actions,
          presetActions: updatedPresetActions
        });

        console.log('✅ Participants saved to cohort:', userIds);
        console.log('✅ selectParticipants action marked as completed');

        // Update the cohort in local state to reflect the change immediately
        setCohorts(prevCohorts =>
          prevCohorts.map(cohort =>
            cohort.id === selectedCohortId
              ? { ...cohort, users: userIds, actions: actions, presetActions: updatedPresetActions }
              : cohort
          )
        );
      } catch (error) {
        console.error('Error saving participants:', error);
        // Don't show error notification to avoid annoying user during drag operations
      }
    };

    // Only save if we have finished loading initial participants (ref check is synchronous)
    if (hasLoadedParticipantsRef.current) {
      saveParticipants();
    }
  }, [selectedParticipants, selectedCohortId, currentTenantId, hasLoadedInitialParticipants]);

  // Real-time listeners for cohort document and matches subcollection
  useEffect(() => {
    if (!selectedCohortId) {
      setPairs([]);
      return;
    }

    // If example cohort is selected, show sample data
    if (selectedCohortId === EXAMPLE_COHORT_ID) {
      console.log('📚 Showing example cohort data');
      setPairs(SAMPLE_PAIRS);
      setUnmatchedMentees(SAMPLE_UNMATCHED_MENTEES);
      setUnmatchedMentors(SAMPLE_UNMATCHED_MENTORS);
      setRecommendations(SAMPLE_RECOMMENDATIONS);
      setDistributionMetrics(SAMPLE_DISTRIBUTION_METRICS);
      return;
    }

    if (!currentTenantId) {
      console.log('ℹ️ No tenant ID available');
      setPairs([]);
      return;
    }

    let cancelled = false;
    matchingNotifiedRef.current = null;
    // Cache of user profiles built once per cohort, extended as needed
    const usersMap = new Map<string, any>();

    // Helper functions to enrich unmatched data with user names
    const enrichUnmatchedMentees = (raw: any[]) => {
      return raw.map((mentee: any) => {
        if (typeof mentee === 'string') {
          const profile = usersMap.get(mentee);
          return {
            uid: mentee,
            mentee_name: profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : null,
            reason: 'No compatible mentors found'
          };
        }
        // Handle case where mentee is an object
        const uid = mentee.uid || mentee.menteeId || mentee.id;
        const profile = uid ? usersMap.get(uid) : null;
        return {
          ...mentee,
          mentee_name: mentee.mentee_name || mentee.menteeName || (profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : null) || mentee.name || mentee.displayName
        };
      });
    };

    const enrichUnmatchedMentors = (raw: any[]) => {
      return raw.map((mentor: any) => {
        if (typeof mentor === 'string') {
          const profile = usersMap.get(mentor);
          return {
            uid: mentor,
            mentor_name: profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : null,
            reason: 'No compatible mentees found'
          };
        }
        const uid = mentor.uid || mentor.mentorId || mentor.id;
        const profile = uid ? usersMap.get(uid) : null;
        return {
          ...mentor,
          mentor_name: mentor.mentor_name || mentor.mentorName || (profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : null) || mentor.name || mentor.displayName
        };
      });
    };

    // Listener A — Cohort document
    const cohortRef = doc(db, 'tenants', currentTenantId, 'cohorts', selectedCohortId);
    const unsubCohort = onSnapshot(cohortRef, async (snapshot) => {
      if (cancelled) return;
      if (!snapshot.exists()) {
        console.error('Cohort document not found');
        return;
      }

      const cohortData = snapshot.data();

      // Update unmatched / recommendations / metrics from the cohort document
      setUnmatchedMentees(enrichUnmatchedMentees(cohortData.unmatchedMentees || []));
      setUnmatchedMentors(enrichUnmatchedMentors(cohortData.unmatchedMentors || []));
      setRecommendations(cohortData.recommendations || []);
      setDistributionMetrics(cohortData.distributionMetrics || null);

      // Sync actions and presetActions so the roadmap updates in real-time
      setCohorts(prev => prev.map(c =>
        c.id === selectedCohortId
          ? { ...c, presetActions: cohortData.presetActions, actions: cohortData.actions }
          : c
      ));

      // Handle matching status updates from background processing
      const matchingStatus = cohortData.matchingStatus as string | undefined;
      setMatchingStep(cohortData.matchingStep || null);

      const resetMatchingState = () => {
        setIsGeneratingMatches(false);
        setMatchingStartedAt(null);
        setMatchingStep(null);
      };

      const clearMatchingFields = (extra: Record<string, any> = {}) => {
        updateDoc(cohortRef, { matchingStatus: null, matchingStep: null, matchingStartedAt: null, ...extra }).catch(() => {});
      };

      const notify = (key: string, fn: () => void) => {
        if (matchingNotifiedRef.current !== key) {
          matchingNotifiedRef.current = key;
          fn();
        }
      };

      if (matchingStatus === 'processing') {
        setIsGeneratingMatches(true);
        const startedAt = cohortData.matchingStartedAt?.toDate?.();
        setMatchingStartedAt(startedAt || null);

        // Stale check: no progress for >30 minutes
        if (startedAt && Date.now() - startedAt.getTime() > 30 * 60 * 1000) {
          resetMatchingState();
          notify('stale', () => {
            notificationService.error('Match generation appears to have stalled. Please try again.');
            clearMatchingFields();
          });
        }
      } else if (matchingStatus === 'completed') {
        resetMatchingState();
        setCohorts(prev => prev.map(c =>
          c.id === selectedCohortId
            ? { ...c, presetActions: cohortData.presetActions, actions: cohortData.actions }
            : c
        ));
        notify('completed', () => {
          notificationService.success('Matches generated successfully!');
          clearMatchingFields();
        });
      } else if (matchingStatus === 'failed') {
        resetMatchingState();
        notify('failed', () => {
          notificationService.error(cohortData.matchingError || 'Failed to generate matches');
          clearMatchingFields({ matchingError: null });
        });
      }

      // Handle email generation status updates from background processing
      const emailGenStatus = cohortData.emailGenerationStatus as { type: string; status: string; error?: string; startedAt?: any } | undefined;
      if (emailGenStatus) {
        const { type, status: genStatus, error: genError } = emailGenStatus;

        const isLoadingSetters: Record<string, (v: boolean) => void> = {
          connection: setIsGeneratingEmails,
          welcomePackage: setIsSendingOnboarding,
          feedback: setIsSendingFeedback,
          checkIn: setIsSendingCheckIn,
          introConsent: setIsGeneratingConsentEmails,
        };
        const actionKeys: Record<string, string> = {
          connection: 'sendMatches',
          welcomePackage: 'welcomePackage',
          feedback: 'collectFeedback',
          checkIn: 'sendCheckIn',
          introConsent: 'sendIntroConsent',
        };
        const scheduledSetters: Record<string, (v: CohortAction | null) => void> = {
          connection: setScheduledSendAction,
          welcomePackage: setScheduledOnboardingAction,
          feedback: setScheduledFeedbackAction,
          checkIn: setScheduledCheckInAction,
          introConsent: setScheduledConsentAction,
        };
        const displayNames: Record<string, string> = {
          connection: 'Connection',
          welcomePackage: 'Onboarding',
          feedback: 'Feedback',
          checkIn: 'Check-in',
          introConsent: 'Consent',
        };

        const setIsLoading = isLoadingSetters[type];
        const actionKey = actionKeys[type];
        const setScheduled = scheduledSetters[type];

        const notifyEmailGen = (key: string, fn: () => void) => {
          if (emailGenNotifiedRef.current !== key) {
            emailGenNotifiedRef.current = key;
            fn();
          }
        };

        const clearEmailGenStatus = () => {
          if (emailGenTimeoutRef.current) clearTimeout(emailGenTimeoutRef.current);
          emailGenTimeoutRef.current = null;
          updateDoc(cohortRef, { emailGenerationStatus: deleteField() })
            .catch((err) => console.warn('Failed to clear emailGenerationStatus:', err));
        };

        // Clear any existing active timeout when status changes
        if (emailGenTimeoutRef.current) {
          clearTimeout(emailGenTimeoutRef.current);
          emailGenTimeoutRef.current = null;
        }

        if (genStatus === 'processing') {
          const startedAt = emailGenStatus.startedAt?.toDate?.() || emailGenStatus.startedAt;
          const elapsed = startedAt ? Date.now() - new Date(startedAt).getTime() : 0;
          const STALE_THRESHOLD = 15 * 60 * 1000;

          if (startedAt && elapsed > STALE_THRESHOLD) {
            // Stale processing status — backend likely crashed without reaching catch
            if (setIsLoading) setIsLoading(false);
            notifyEmailGen(`${type}-stale`, () => {
              notificationService.error('Email generation appears to have stalled. Please try again.');
              clearEmailGenStatus();
            });
          } else {
            if (setIsLoading) setIsLoading(true);
            // Active timeout: if no completed/failed snapshot arrives within the remaining window,
            // reset the UI so the user isn't stuck in loading state forever
            const remaining = STALE_THRESHOLD - elapsed;
            emailGenTimeoutRef.current = setTimeout(() => {
              emailGenTimeoutRef.current = null;
              if (setIsLoading) setIsLoading(false);
              notifyEmailGen(`${type}-stale`, () => {
                notificationService.error('Email generation appears to have stalled. Please try again.');
                clearEmailGenStatus();
              });
            }, remaining);
          }
        } else if (genStatus === 'completed') {
          if (setIsLoading) setIsLoading(false);
          if (setScheduled && actionKey) {
            const actions: any[] = cohortData.actions || [];
            const latestAction = actions
              .filter((a: any) => a.action === actionKey && (a.status === 'active' || a.status === 'completed'))
              .sort((a: any, b: any) => (b.createdAt?.toDate?.()?.getTime() || 0) - (a.createdAt?.toDate?.()?.getTime() || 0))[0];
            if (latestAction) {
              setScheduled({
                ...latestAction,
                scheduledAt: latestAction.scheduledAt?.toDate?.() || latestAction.scheduledAt,
                createdAt: latestAction.createdAt?.toDate?.() || latestAction.createdAt,
                completedAt: latestAction.completedAt?.toDate?.() || latestAction.completedAt,
              });
            }
          }
          notifyEmailGen(`${type}-completed`, () => {
            notificationService.success(`${displayNames[type] || type} emails scheduled! Emails will be sent in 24 hours.`);
            clearEmailGenStatus();
          });
        } else if (genStatus === 'failed') {
          if (setIsLoading) setIsLoading(false);
          notifyEmailGen(`${type}-failed`, () => {
            notificationService.error(genError || `Failed to generate ${type} emails`);
            clearEmailGenStatus();
          });
        }
      }

      // Detect changes to the users[] array and update selectedParticipants
      const cohortUserIds: string[] = cohortData.users || [];
      const prevIds = prevCohortUsersRef.current;
      const usersChanged = cohortUserIds.length !== prevIds.length ||
        cohortUserIds.some((id, i) => id !== prevIds[i]);

      if (usersChanged) {
        prevCohortUsersRef.current = cohortUserIds;

        if (cohortUserIds.length > 0) {
          try {
            // Fetch all tenant users (for the participants list) if not already loaded
            const allTenantUsers = await userDataService.fetchUsersForTenant(currentTenantId);
            if (cancelled) return;
            setAllUsers(allTenantUsers);

            // Also populate / refresh usersMap for enrichment
            await Promise.all(
              cohortUserIds.map(async (uid: string) => {
                if (!usersMap.has(uid)) {
                  try {
                    const profile = await userDataService.fetchUserProfile(uid);
                    if (profile) usersMap.set(uid, profile);
                  } catch (err) {
                    console.warn(`Failed to fetch profile for user ${uid}:`, err);
                  }
                }
              })
            );
            if (cancelled) return;

            const selectedUsers = allTenantUsers.filter(user => cohortUserIds.includes(user.uid));
            setSelectedParticipants(selectedUsers);
            setHasLoadedInitialParticipants(true);
            hasLoadedParticipantsRef.current = true;
            console.log('Loaded participants from cohort snapshot:', selectedUsers.length);
          } catch (error) {
            if (!cancelled) console.error('Error loading participants from cohort snapshot:', error);
          }
        } else {
          setHasLoadedInitialParticipants(true);
          hasLoadedParticipantsRef.current = true;
        }
      } else if (!hasLoadedParticipantsRef.current) {
        // First snapshot but users array is same as prev (empty initial case)
        setHasLoadedInitialParticipants(true);
        hasLoadedParticipantsRef.current = true;
      }
    }, (error) => {
      if (!cancelled) {
        console.error('Cohort snapshot error:', error);
      }
    });

    // Listener B — Matches subcollection
    const matchesRef = collection(db, 'tenants', currentTenantId, 'cohorts', selectedCohortId, 'matches');
    const unsubMatches = onSnapshot(matchesRef, async (snapshot) => {
      if (cancelled) return;

      // Collect all user IDs referenced in matches so we can fetch any missing profiles
      const neededIds = new Set<string>();
      snapshot.docs.forEach((matchDoc) => {
        const d = matchDoc.data();
        if (d.menteeId) neededIds.add(d.menteeId);
        if (d.mentorId) neededIds.add(d.mentorId);
      });

      // Fetch any profiles not yet in usersMap
      const toFetch = Array.from(neededIds).filter(id => !usersMap.has(id));
      if (toFetch.length > 0) {
        await Promise.all(
          toFetch.map(async (uid) => {
            try {
              const profile = await userDataService.fetchUserProfile(uid);
              if (profile) usersMap.set(uid, profile);
            } catch (err) {
              console.warn(`Failed to fetch profile for user ${uid}:`, err);
            }
          })
        );
      }
      if (cancelled) return;

      const loadedPairs: MentorshipPair[] = [];

      snapshot.docs.forEach((matchDoc) => {
        const matchData = matchDoc.data();
        const menteeId = matchData.menteeId;
        const mentorId = matchData.mentorId;

        const menteeProfile = usersMap.get(menteeId);
        const mentorProfile = usersMap.get(mentorId);

        if (menteeId && mentorId && menteeProfile && mentorProfile) {
          const menteeRole = menteeProfile.works?.[0]?.position || 'Mentee';
          const mentorRole = mentorProfile.works?.[0]?.position || 'Mentor';
          const menteeCompany = menteeProfile.works?.[0]?.companyName || '';
          const mentorCompany = mentorProfile.works?.[0]?.companyName || '';
          const menteeRoleDisplay = menteeCompany ? `${menteeRole} at ${menteeCompany}` : menteeRole;
          const mentorRoleDisplay = mentorCompany ? `${mentorRole} at ${mentorCompany}` : mentorRole;

          loadedPairs.push({
            id: matchDoc.id,
            mentee: {
              uid: menteeId,
              firstName: menteeProfile.firstName || 'Unknown',
              lastName: menteeProfile.lastName || 'Mentee',
              email: menteeProfile.email || `${menteeId}@unknown.com`,
              role: menteeRoleDisplay,
              bio: menteeProfile.quote || '',
              skills: menteeProfile.works?.[0]?.field || [],
              interests: menteeProfile.interests || []
            },
            mentor: {
              uid: mentorId,
              firstName: mentorProfile.firstName || 'Unknown',
              lastName: mentorProfile.lastName || 'Mentor',
              email: mentorProfile.email || `${mentorId}@unknown.com`,
              role: mentorRoleDisplay,
              bio: mentorProfile.quote || '',
              skills: mentorProfile.works?.[0]?.field || [],
              interests: mentorProfile.interests || []
            },
            matchReason: matchData.matchReason || matchData.match_reasons || matchData.reasoning || '',
            status: (matchData.status || 'pending') as 'pending' | 'sent' | 'declined',
            sentAt: matchData.matchedAt ? matchData.matchedAt.toDate() : undefined,
            matchMetrics: matchData.matchMetrics || [],
            matchScore: matchData.matchScore || {}
          });
        } else {
          console.warn(`Missing user profile for match ${matchDoc.id}: menteeId=${menteeId}, mentorId=${mentorId}`);
        }
      });

      setPairs(loadedPairs);
      console.log(`Matches snapshot: ${loadedPairs.length} pairs loaded`);
    }, (error) => {
      if (!cancelled) {
        console.error('Matches snapshot error:', error);
        notificationService.error('Failed to load matches. Please check the console for details.');
      }
    });

    return () => {
      cancelled = true;
      unsubCohort();
      unsubMatches();
      if (emailGenTimeoutRef.current) {
        clearTimeout(emailGenTimeoutRef.current);
        emailGenTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId, selectedCohortId]);

  const pendingPairs = pairs.filter(p => p.status === 'pending');

  // Dynamically compute unmatched mentees and mentors based on selectedParticipants and pairs
  const displayUnmatchedMentees = useMemo(() => {
    const matchedMenteeIds = new Set(pairs.map(p => p.mentee.uid));
    return selectedParticipants
      .filter(user => user.profile?.mentor !== true) // Is a mentee
      .filter(user => !matchedMenteeIds.has(user.uid)) // Not in any match
      .map(user => ({
        name: user.displayName,
        uid: user.uid,
        email: user.profile?.email || user.authEmail,
        role: user.profile?.works?.[0]?.position || 'Mentee'
      }));
  }, [selectedParticipants, pairs]);

  const displayUnmatchedMentors = useMemo(() => {
    const matchedMentorIds = new Set(pairs.map(p => p.mentor.uid));
    return selectedParticipants
      .filter(user => user.profile?.mentor === true) // Is a mentor
      .filter(user => !matchedMentorIds.has(user.uid)) // Not in any match
      .map(user => ({
        name: user.displayName,
        uid: user.uid,
        email: user.profile?.email || user.authEmail,
        role: user.profile?.works?.[0]?.position || 'Mentor'
      }));
  }, [selectedParticipants, pairs]);

  // Dynamically compute distribution metrics based on pairs and selectedParticipants
  const displayDistributionMetrics = useMemo(() => {
    const mentors = selectedParticipants.filter(user => user.profile?.mentor === true);
    if (mentors.length === 0) return null;

    // Count mentees per mentor
    const menteeCountByMentor: Record<string, number> = {};
    mentors.forEach(mentor => {
      menteeCountByMentor[mentor.uid] = 0;
    });
    pairs.forEach(pair => {
      if (menteeCountByMentor[pair.mentor.uid] !== undefined) {
        menteeCountByMentor[pair.mentor.uid]++;
      }
    });

    const menteeCounts = Object.values(menteeCountByMentor);
    const totalMentees = menteeCounts.reduce((sum, count) => sum + count, 0);
    const totalMentors = mentors.length;

    // Average mentees per mentor
    const average = totalMentors > 0 ? totalMentees / totalMentors : 0;

    // Standard deviation
    const squaredDiffs = menteeCounts.map(count => Math.pow(count - average, 2));
    const avgSquaredDiff = squaredDiffs.reduce((sum, diff) => sum + diff, 0) / totalMentors;
    const standardDeviation = Math.sqrt(avgSquaredDiff);

    // Load balance score (1 = perfectly balanced, 0 = very unbalanced)
    // Normalize standard deviation relative to the average
    const loadBalanceScore = average > 0 ? Math.max(0, 1 - (standardDeviation / average)) : 1;

    // Count mentors by number of mentees
    const distribution: Record<string, number> = {
      mentors_with_0_mentees: 0,
      mentors_with_1_mentee: 0,
      mentors_with_2_mentees: 0,
      mentors_with_3_mentees: 0,
      mentors_with_4_mentees: 0,
      mentors_with_5_mentees: 0,
    };

    menteeCounts.forEach(count => {
      if (count === 0) distribution.mentors_with_0_mentees++;
      else if (count === 1) distribution.mentors_with_1_mentee++;
      else if (count === 2) distribution.mentors_with_2_mentees++;
      else if (count === 3) distribution.mentors_with_3_mentees++;
      else if (count === 4) distribution.mentors_with_4_mentees++;
      else distribution.mentors_with_5_mentees++; // 5 or more
    });

    return {
      average_mentees_per_mentor: average,
      load_balance_score: loadBalanceScore,
      standard_deviation: standardDeviation,
      ...distribution
    };
  }, [selectedParticipants, pairs]);

  const displayRecommendations = recommendations;

  // Filter pairs based on search terms
  const filteredPendingPairs = pendingPairs.filter(pair => {
    if (!recommendedSearch) return true;
    const searchTerm = recommendedSearch.toLowerCase();
    return (
      pair.mentee.firstName.toLowerCase().includes(searchTerm) ||
      pair.mentee.lastName.toLowerCase().includes(searchTerm) ||
      pair.mentor.firstName.toLowerCase().includes(searchTerm) ||
      pair.mentor.lastName.toLowerCase().includes(searchTerm) ||
      pair.mentee.role.toLowerCase().includes(searchTerm) ||
      pair.mentor.role.toLowerCase().includes(searchTerm) ||
      pair.matchReason.toLowerCase().includes(searchTerm)
    );
  });

  // Group filtered pairs by mentor
  const groupedByMentor = useMemo(() => {
    const groups: { mentor: MentorshipPair['mentor']; pairs: MentorshipPair[] }[] = [];
    const mentorMap = new Map<string, number>();
    for (const pair of filteredPendingPairs) {
      const idx = mentorMap.get(pair.mentor.uid);
      if (idx !== undefined) {
        groups[idx].pairs.push(pair);
      } else {
        mentorMap.set(pair.mentor.uid, groups.length);
        groups.push({ mentor: pair.mentor, pairs: [pair] });
      }
    }
    return groups;
  }, [filteredPendingPairs]);

  return (
    <div className="mt-8 w-full min-h-0">
      {/* Cohort Selector */}
      <div className="mb-6">
        <div className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-4">
            <label htmlFor="cohort-select" className="text-sm font-medium text-navy-700 dark:text-white whitespace-nowrap">
              Select Cohort:
            </label>
            <select
              id="cohort-select"
              value={selectedCohortId || ''}
              onChange={async (e) => {
                const value = e.target.value;
                if (value === '__CREATE_NEW__') {
                  // Navigate to workflows page to create a new cohort
                  navigate('/admin/intros/workflows');
                  return;
                }
                setSelectedCohortId(value);
              }}
              disabled={isLoadingCohorts}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingCohorts ? (
                <option value="">Loading cohorts...</option>
              ) : (
                <>
                  <option value="__CREATE_NEW__" className="text-green-600 font-medium">
                    Create New Cohort...
                  </option>
                  <option disabled>─────────────────</option>
                  {cohorts.map((cohort) => {
                    const workflowLabel = cohort.workflowType === 'automated-double-opt-in'
                      ? 'Double Opt-In'
                      : cohort.workflowType === 'automated'
                        ? 'Automated'
                        : cohort.workflowType === 'custom'
                          ? 'Custom'
                          : 'Standard';
                    return (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.id === EXAMPLE_COHORT_ID
                          ? `${cohort.title}${cohort.description ? ` - ${cohort.description}` : ''}`
                          : `[${workflowLabel}] ${cohort.title} - ${cohort.createdAt.toLocaleDateString()} - ${cohort.users?.length || 0} users`
                        }
                      </option>
                    );
                  })}
                </>
              )}
            </select>
            <button
              onClick={() => setIsCohortSettingsOpen(true)}
              disabled={!selectedCohortId}
              className="px-3 py-2 rounded-lg border transition-all flex items-center justify-center gap-2 bg-gray-50 dark:bg-navy-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-navy-700 hover:border-gray-300 dark:hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Cohort Settings"
            >
              <MdSettings className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Cohort Roadmap */}
          {selectedCohortId && (() => {
            const selectedCohort = cohorts.find(c => c.id === selectedCohortId);
            if (!selectedCohort) return null;
            return (
              <CohortRoadmap
                cohort={selectedCohort}
                onOpenStep={(stepName) => setOpenStepPopup(stepName)}
              />
            );
          })()}

          {/* Step Action Popup Modal */}
          {openStepPopup && (
            <div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
              onMouseDown={(e) => {
                popupMouseDownOnBackdropRef.current = e.target === e.currentTarget;
              }}
              onMouseUp={(e) => {
                const startedOnBackdrop = popupMouseDownOnBackdropRef.current;
                popupMouseDownOnBackdropRef.current = false;
                if (!isGeneratingMatches && startedOnBackdrop && e.target === e.currentTarget) {
                  setOpenStepPopup(null);
                }
              }}
            >
              <div
                className="modal-content bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-6xl w-full p-6 max-h-[80vh] overflow-y-auto"
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                    {openStepPopup === 'selectParticipants' && 'Select Participants'}
                    {openStepPopup === 'onboarding' && 'Onboarding Materials'}
                    {openStepPopup === 'createMatches' && 'Create Matches'}
                    {openStepPopup === 'launch' && 'Launch 🚀'}
                    {openStepPopup === 'introduceAndConnect' && 'Introduce & Connect'}
                    {openStepPopup === 'collectFeedback' && 'Collect Feedback'}
                    {openStepPopup === 'checkIn' && 'Check-in Email'}
                    {openStepPopup === 'manualMatch' && 'Add Match Manually'}
                  </h3>
                  <button
                    onClick={() => {
                      if (!isGeneratingMatches) {
                        setOpenStepPopup(null);
                      }
                    }}
                    disabled={isGeneratingMatches}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:text-gray-300 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <MdClose className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  {openStepPopup === 'selectParticipants' && (
                    <>
                      {loadingUsers ? (
                        <div className="text-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto mb-3"></div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Loading users...</p>
                        </div>
                      ) : (
                        <>
                          <LockBanner lockState={cohortLockState} />

                          {/* Search Bar */}
                          <div className="relative">
                            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search users..."
                              value={participantSearch}
                              onChange={(e) => setParticipantSearch(e.target.value)}
                              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            />
                          </div>

                          {/* Quick Action Button */}
                          <button
                            onClick={handleSelectAllParticipants}
                            disabled={isCohortLocked}
                            title={isCohortLocked ? lockTooltip : undefined}
                            className="text-sm text-brand-500 hover:text-brand-600 dark:hover:text-brand-400 font-medium disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                          >
                            Select All Users
                          </button>

                          <div className="grid grid-cols-2 gap-4 max-h-96">
                            {/* Available Users Column */}
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                              <div className="bg-gray-50 dark:bg-navy-900 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                                <h4 className="text-sm font-semibold text-navy-700 dark:text-white">
                                  Available Users ({allUsers.filter(u => {
                                    if (pendingParticipants.find(p => p.uid === u.uid)) return false;
                                    if (participantSearch === '') return true;

                                    const searchLower = participantSearch.toLowerCase();
                                    if (u.displayName.toLowerCase().includes(searchLower)) return true;
                                    if (u.profile?.email?.toLowerCase().includes(searchLower)) return true;
                                    if (u.authEmail?.toLowerCase().includes(searchLower)) return true;
                                    if (u.profile?.works?.some(work =>
                                      work.position?.toLowerCase().includes(searchLower) ||
                                      work.companyName?.toLowerCase().includes(searchLower)
                                    )) return true;
                                    if (u.profile?.educations?.some(edu =>
                                      edu.level?.toLowerCase().includes(searchLower) ||
                                      edu.collegeName?.toLowerCase().includes(searchLower) ||
                                      edu.majors?.some(major => major.toLowerCase().includes(searchLower))
                                    )) return true;
                                    if (u.profile?.interests?.some(interest =>
                                      interest.toLowerCase().includes(searchLower)
                                    )) return true;
                                    const role = u.profile?.mentor ? 'mentor' : 'mentee';
                                    if (role.includes(searchLower)) return true;

                                    return false;
                                  }).length})
                                </h4>
                              </div>
                              <div className="overflow-y-auto max-h-80 p-2 space-y-1">
                                {allUsers
                                  .filter(user => !pendingParticipants.find(p => p.uid === user.uid))
                                  .filter(user => {
                                    if (participantSearch === '') return true;
                                    const searchLower = participantSearch.toLowerCase();

                                    // Search in basic info
                                    if (user.displayName.toLowerCase().includes(searchLower)) return true;
                                    if (user.profile?.email?.toLowerCase().includes(searchLower)) return true;
                                    if (user.authEmail?.toLowerCase().includes(searchLower)) return true;

                                    // Search in work info
                                    if (user.profile?.works?.some(work =>
                                      work.position?.toLowerCase().includes(searchLower) ||
                                      work.companyName?.toLowerCase().includes(searchLower)
                                    )) return true;

                                    // Search in education info
                                    if (user.profile?.educations?.some(edu =>
                                      edu.level?.toLowerCase().includes(searchLower) ||
                                      edu.collegeName?.toLowerCase().includes(searchLower) ||
                                      edu.majors?.some(major => major.toLowerCase().includes(searchLower))
                                    )) return true;

                                    // Search in interests
                                    if (user.profile?.interests?.some(interest =>
                                      interest.toLowerCase().includes(searchLower)
                                    )) return true;

                                    // Search in role type
                                    const role = user.profile?.mentor ? 'mentor' : 'mentee';
                                    if (role.includes(searchLower)) return true;

                                    return false;
                                  })
                                  .map((user) => (
                                    <div
                                      key={user.uid}
                                      draggable={!isCohortLocked}
                                      onDragStart={(e) => {
                                        if (isCohortLocked) {
                                          e.preventDefault();
                                          return;
                                        }
                                        e.dataTransfer.setData('userId', user.uid);
                                        e.dataTransfer.effectAllowed = 'move';
                                      }}
                                      className={`flex items-start gap-2 p-3 bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors group ${isCohortLocked ? 'opacity-75 cursor-not-allowed' : 'hover:border-brand-500 dark:hover:border-brand-400'}`}
                                    >
                                      <MdDragIndicator className={`h-4 w-4 mt-1 flex-shrink-0 ${isCohortLocked ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 group-hover:text-brand-500'}`} />
                                      <Avatar uid={user.uid} displayName={user.displayName} size="small" />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                          <p className="text-sm font-medium text-navy-700 dark:text-white truncate">
                                            {user.displayName}
                                          </p>
                                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                                            user.profile?.mentor
                                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                          }`}>
                                            {user.profile?.mentor ? 'Mentor' : 'Mentee'}
                                          </span>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1">
                                          {user.profile?.email || user.authEmail}
                                        </p>
                                        <div className="space-y-1">
                                          {user.profile?.works && user.profile.works.length > 0 && (
                                            <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                                              {user.profile.works[0].position} @ {user.profile.works[0].companyName}
                                            </div>
                                          )}
                                          {user.profile?.educations && user.profile.educations.length > 0 && (
                                            <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                                              {user.profile.educations[0].level} @ {user.profile.educations[0].collegeName}
                                            </div>
                                          )}
                                          {user.profile?.interests && user.profile.interests.length > 0 && (
                                            <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                                              {user.profile.interests.slice(0, 3).join(', ')}
                                              {user.profile.interests.length > 3 && ` +${user.profile.interests.length - 3}`}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex flex-col gap-1 flex-shrink-0">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setViewingUser(user);
                                            setIsProfileModalOpen(true);
                                          }}
                                          className="p-1 hover:bg-gray-100 dark:hover:bg-navy-700 rounded transition-colors"
                                          title="View profile"
                                        >
                                          <MdVisibility className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddParticipant(user);
                                          }}
                                          disabled={isCohortLocked}
                                          className="p-1 hover:bg-brand-50 dark:hover:bg-brand-900/30 rounded transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                          title={isCohortLocked ? lockTooltip : 'Add participant'}
                                        >
                                          <MdAdd className={`h-4 w-4 ${isCohortLocked ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 group-hover:text-brand-500'}`} />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                {allUsers.filter(u => {
                                  if (pendingParticipants.find(p => p.uid === u.uid)) return false;
                                  if (participantSearch === '') return true;

                                  const searchLower = participantSearch.toLowerCase();
                                  if (u.displayName.toLowerCase().includes(searchLower)) return true;
                                  if (u.profile?.email?.toLowerCase().includes(searchLower)) return true;
                                  if (u.authEmail?.toLowerCase().includes(searchLower)) return true;
                                  if (u.profile?.works?.some(work =>
                                    work.position?.toLowerCase().includes(searchLower) ||
                                    work.companyName?.toLowerCase().includes(searchLower)
                                  )) return true;
                                  if (u.profile?.educations?.some(edu =>
                                    edu.level?.toLowerCase().includes(searchLower) ||
                                    edu.collegeName?.toLowerCase().includes(searchLower) ||
                                    edu.majors?.some(major => major.toLowerCase().includes(searchLower))
                                  )) return true;
                                  if (u.profile?.interests?.some(interest =>
                                    interest.toLowerCase().includes(searchLower)
                                  )) return true;
                                  const role = u.profile?.mentor ? 'mentor' : 'mentee';
                                  if (role.includes(searchLower)) return true;

                                  return false;
                                }).length === 0 && (
                                  <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                                    No users available
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Selected Participants Column */}
                            <div
                              className={`border-2 border-dashed rounded-lg overflow-hidden ${isCohortLocked ? 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20' : 'border-brand-300 dark:border-brand-700 bg-brand-50/50 dark:bg-brand-900/10'}`}
                              onDragOver={(e) => {
                                if (isCohortLocked) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                              }}
                              onDrop={(e) => {
                                if (isCohortLocked) return;
                                e.preventDefault();
                                const userId = e.dataTransfer.getData('userId');
                                const user = allUsers.find(u => u.uid === userId);
                                if (user && !pendingParticipants.find(p => p.uid === userId)) {
                                  setPendingParticipants([...pendingParticipants, user]);
                                }
                              }}
                            >
                              <div className="bg-brand-100 dark:bg-brand-900/30 px-3 py-2 border-b border-brand-200 dark:border-brand-700">
                                <h4 className="text-sm font-semibold text-brand-700 dark:text-brand-300">
                                  Selected Participants ({pendingParticipants.length})
                                </h4>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">
                                    {pendingParticipants.filter(p => p.profile?.mentor).length} Mentors
                                  </span>
                                  <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">
                                    {pendingParticipants.filter(p => !p.profile?.mentor).length} Mentees
                                  </span>
                                </div>
                              </div>
                              <div className="overflow-y-auto max-h-80 p-2 space-y-1">
                                {pendingParticipants
                                  .filter(user => {
                                    if (participantSearch === '') return true;
                                    const searchLower = participantSearch.toLowerCase();

                                    // Search in basic info
                                    if (user.displayName.toLowerCase().includes(searchLower)) return true;
                                    if (user.profile?.email?.toLowerCase().includes(searchLower)) return true;
                                    if (user.authEmail?.toLowerCase().includes(searchLower)) return true;

                                    // Search in work info
                                    if (user.profile?.works?.some(work =>
                                      work.position?.toLowerCase().includes(searchLower) ||
                                      work.companyName?.toLowerCase().includes(searchLower)
                                    )) return true;

                                    // Search in education info
                                    if (user.profile?.educations?.some(edu =>
                                      edu.level?.toLowerCase().includes(searchLower) ||
                                      edu.collegeName?.toLowerCase().includes(searchLower) ||
                                      edu.majors?.some(major => major.toLowerCase().includes(searchLower))
                                    )) return true;

                                    // Search in interests
                                    if (user.profile?.interests?.some(interest =>
                                      interest.toLowerCase().includes(searchLower)
                                    )) return true;

                                    // Search in role type
                                    const role = user.profile?.mentor ? 'mentor' : 'mentee';
                                    if (role.includes(searchLower)) return true;

                                    return false;
                                  })
                                  .map((user) => (
                                  <div
                                    key={user.uid}
                                    draggable={!isCohortLocked}
                                    onDragStart={(e) => {
                                      if (isCohortLocked) {
                                        e.preventDefault();
                                        return;
                                      }
                                      e.dataTransfer.setData('userId', user.uid);
                                      e.dataTransfer.effectAllowed = 'move';
                                    }}
                                    className={`flex items-start gap-2 p-3 bg-white dark:bg-navy-800 border rounded-lg transition-colors group ${isCohortLocked ? 'border-gray-200 dark:border-gray-700 opacity-75 cursor-not-allowed' : 'border-brand-200 dark:border-brand-700 hover:border-brand-500 dark:hover:border-brand-400'}`}
                                  >
                                    <MdDragIndicator className={`h-4 w-4 mt-1 flex-shrink-0 ${isCohortLocked ? 'text-gray-300 dark:text-gray-600' : 'text-brand-400 group-hover:text-brand-500'}`} />
                                    <Avatar uid={user.uid} displayName={user.displayName} size="small" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <p className="text-sm font-medium text-navy-700 dark:text-white truncate">
                                          {user.displayName}
                                        </p>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                                          user.profile?.mentor
                                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                        }`}>
                                          {user.profile?.mentor ? 'Mentor' : 'Mentee'}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1">
                                        {user.profile?.email || user.authEmail}
                                      </p>
                                      <div className="space-y-1">
                                        {user.profile?.works && user.profile.works.length > 0 && (
                                          <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                                            {user.profile.works[0].position} @ {user.profile.works[0].companyName}
                                          </div>
                                        )}
                                        {user.profile?.educations && user.profile.educations.length > 0 && (
                                          <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                                            {user.profile.educations[0].level} @ {user.profile.educations[0].collegeName}
                                          </div>
                                        )}
                                        {user.profile?.interests && user.profile.interests.length > 0 && (
                                          <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                                            {user.profile.interests.slice(0, 3).join(', ')}
                                            {user.profile.interests.length > 3 && ` +${user.profile.interests.length - 3}`}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-1 flex-shrink-0">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setViewingUser(user);
                                          setIsProfileModalOpen(true);
                                        }}
                                        className="p-1 hover:bg-gray-100 dark:hover:bg-navy-700 rounded transition-colors"
                                        title="View profile"
                                      >
                                        <MdVisibility className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveParticipant(user);
                                        }}
                                        disabled={isCohortLocked}
                                        className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                        title={isCohortLocked ? lockTooltip : 'Remove participant'}
                                      >
                                        <MdClose className={`h-4 w-4 ${isCohortLocked ? 'text-gray-300 dark:text-gray-600' : 'text-red-500'}`} />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                {pendingParticipants.length === 0 && (
                                  <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
                                    <MdPeople className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                                    <p>Drag users here or click to add</p>
                                  </div>
                                )}
                                {pendingParticipants.length > 0 && participantSearch !== '' && pendingParticipants.filter(user => {
                                  const searchLower = participantSearch.toLowerCase();
                                  if (user.displayName.toLowerCase().includes(searchLower)) return true;
                                  if (user.profile?.email?.toLowerCase().includes(searchLower)) return true;
                                  if (user.authEmail?.toLowerCase().includes(searchLower)) return true;
                                  if (user.profile?.works?.some(work =>
                                    work.position?.toLowerCase().includes(searchLower) ||
                                    work.companyName?.toLowerCase().includes(searchLower)
                                  )) return true;
                                  if (user.profile?.educations?.some(edu =>
                                    edu.level?.toLowerCase().includes(searchLower) ||
                                    edu.collegeName?.toLowerCase().includes(searchLower) ||
                                    edu.majors?.some(major => major.toLowerCase().includes(searchLower))
                                  )) return true;
                                  if (user.profile?.interests?.some(interest =>
                                    interest.toLowerCase().includes(searchLower)
                                  )) return true;
                                  const role = user.profile?.mentor ? 'mentor' : 'mentee';
                                  if (role.includes(searchLower)) return true;
                                  return false;
                                }).length === 0 && (
                                  <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                                    No selected participants match your search
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-3 pt-4">
                            <button
                              onClick={() => {
                                if (isCohortLocked) {
                                  setOpenStepPopup(null);
                                  return;
                                }
                                handleClearAllParticipants();
                              }}
                              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              {isCohortLocked ? 'Close' : 'Cancel'}
                            </button>
                            <button
                              onClick={() => {
                                if (selectedCohortId === EXAMPLE_COHORT_ID) {
                                  notificationService.warning('Cannot modify the example cohort. Please create a new cohort first.');
                                  return;
                                }
                                if (pendingParticipants.length === 0) {
                                  notificationService.warning('Please select at least one participant');
                                  return;
                                }
                                // Apply pending changes to actual state
                                setSelectedParticipants(pendingParticipants);
                                notificationService.success(`${pendingParticipants.length} participants saved`);
                                setOpenStepPopup(null);
                              }}
                              disabled={selectedCohortId === EXAMPLE_COHORT_ID || isCohortLocked}
                              title={isCohortLocked ? lockTooltip : undefined}
                              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Save ({pendingParticipants.length})
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {openStepPopup === 'onboarding' && (
                    <>
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                        <div className="flex items-start gap-2">
                          <MdInfo className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-3">
                              Send personalized onboarding materials to all participants
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              💡 Emails will be sent <strong>24 hours after scheduling</strong>. Customized send time coming soon.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* No participants warning */}
                      {(cohorts.find(c => c.id === selectedCohortId)?.users?.length || 0) === 0 && !scheduledOnboardingAction && (
                        <div className="mb-4 p-4 rounded-lg border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
                          <div className="flex items-start gap-2">
                            <MdInfo className="h-5 w-5 flex-shrink-0 mt-0.5 text-yellow-600 dark:text-yellow-400" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-navy-700 dark:text-white mb-1">
                                No Participants Selected
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                Please select participants before sending onboarding materials. Go to the "Select Participants" step to add users to this cohort.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <ActionStatusBar
                          action={scheduledOnboardingAction}
                          label="Onboarding"
                          notScheduledText="No emails currently scheduled. Click the button below to schedule onboarding emails."
                        />
                      </div>

                      {/* Onboarding Material Input */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-navy-700 dark:text-white mb-1">
                          Custom Onboarding Content <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Add a onboarding link or custom message. If provided, it will be included in the onboarding email.
                        </p>
                        <div className={`relative border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden ${scheduledOnboardingAction
                            ? 'bg-gray-100 dark:bg-gray-900'
                            : 'bg-white dark:bg-navy-800'
                          }`}>
                          <textarea
                            value={onboardingMaterial}
                            onChange={(e) => setOnboardingMaterial(e.target.value)}
                            placeholder="Add a onboarding link or additional instructions (e.g., https://docs.google.com/presentation/d/xyz)"
                            rows={3}
                            maxLength={1000}
                            disabled={!!scheduledOnboardingAction}
                            className="w-full px-4 py-2 text-sm bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none disabled:cursor-not-allowed resize-none disabled:text-gray-600 dark:disabled:text-gray-400"
                          />
                          <span className="absolute bottom-12 right-4 text-xs text-gray-400">{onboardingMaterial.length}/1000</span>
                          <div className="px-4 pb-2 flex justify-end border-t border-gray-200 dark:border-gray-700 pt-2">
                            <button
                              onClick={async () => {
                                if (!currentTenantId || !selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID) {
                                  return;
                                }

                                setIsSavingOnboardingMaterial(true);
                                try {
                                  const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                  await updateDoc(cohortRef, {
                                    customWelcomePackageContent: onboardingMaterial.trim()
                                  });
                                  notificationService.success('Onboarding material saved successfully');
                                  console.log('✅ Onboarding material saved');

                                  // Custom content changed — wipe both cached roles, then refetch
                                  // the current one so the admin sees the updated preview immediately.
                                  setWelcomePackageCache({});
                                  setIsLoadingWelcomePreview(true);
                                  try {
                                    const previewResult = await generateEmails({
                                      tenant_id: currentTenantId,
                                      cohort_id: selectedCohortId,
                                      email_type: 'welcomePackage',
                                      preview: true,
                                      preview_role: previewRole
                                    });
                                    const freshSample =
                                      previewResult.success && previewResult.data?.sample
                                        ? previewResult.data.sample
                                        : null;
                                    setWelcomePackageCache({ [previewRole]: freshSample });
                                  } catch (previewError) {
                                    console.error('Error regenerating welcome package preview:', previewError);
                                    notificationService.error('Failed to regenerate email preview');
                                  } finally {
                                    setIsLoadingWelcomePreview(false);
                                  }
                                } catch (error) {
                                  console.error('Error saving onboarding material:', error);
                                  notificationService.error('Failed to save onboarding material');
                                } finally {
                                  setIsSavingOnboardingMaterial(false);
                                }
                              }}
                              disabled={!!scheduledOnboardingAction || isSavingOnboardingMaterial || selectedCohortId === EXAMPLE_COHORT_ID}
                              className="px-4 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              {isSavingOnboardingMaterial ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Example Email Preview */}
                      <div className="mt-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => setIsExampleOnboardingEmailExpanded(!isExampleOnboardingEmailExpanded)}
                          className="w-full p-4 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-lg"
                        >
                          <h4 className="font-semibold text-navy-700 dark:text-white">📧 Example Welcome Package Email</h4>
                          {isExampleOnboardingEmailExpanded ? (
                            <MdExpandLess className="h-5 w-5 text-gray-500" />
                          ) : (
                            <MdExpandMore className="h-5 w-5 text-gray-500" />
                          )}
                        </button>

                        {isExampleOnboardingEmailExpanded && (
                          <div className="px-4 pb-4">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Preview as:</span>
                              <div className="inline-flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
                                <button
                                  type="button"
                                  onClick={() => setPreviewRole('mentee')}
                                  className={`px-3 py-1 text-xs font-medium transition-colors ${previewRole === 'mentee' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                >
                                  {customLabels.mentee || 'Mentee'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPreviewRole('mentor')}
                                  className={`px-3 py-1 text-xs font-medium transition-colors ${previewRole === 'mentor' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                >
                                  {customLabels.mentor || 'Mentor'}
                                </button>
                              </div>
                            </div>
                            {isLoadingWelcomePreview ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Generating preview live</span>
                              </div>
                            ) : welcomePackageEmailExample ? (
                              <div className="space-y-2 text-xs">
                                <div><strong>To:</strong> {welcomePackageEmailExample.to}</div>
                                <div><strong>Subject:</strong> {welcomePackageEmailExample.subject}</div>
                                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                  <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                                    {welcomePackageEmailExample.content}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 dark:text-gray-400 py-4">
                                No preview available
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Hold Button to Schedule */}
                      <div className="mt-6 space-y-3">
                        <div>
                          <button
                            onMouseDown={() => {
                              console.log('🖱️ Mouse down - starting onboarding hold');
                              setIsHoldingOnboarding(true);
                              setOnboardingHoldProgress(0);

                              let progress = 0;
                              const interval = setInterval(() => {
                                progress += 1;
                                setOnboardingHoldProgress(progress);

                                if (progress >= 100) {
                                  clearInterval(interval);
                                  setIsHoldingOnboarding(false);
                                  setOnboardingHoldProgress(0);

                                  if (scheduledOnboardingAction?.status === 'active') {
                                    console.log('🗑️ Canceling scheduled onboarding');

                                    // Cancel scheduled onboarding
                                    (async () => {
                                      setIsSendingOnboarding(true);

                                      try {
                                        if (scheduledOnboardingAction.scheduledTaskID) {

                                                                                    const cancelTask = httpsCallable(functions, 'cancelScheduledTask');

                                          const result: any = await cancelTask({ taskName: scheduledOnboardingAction.scheduledTaskID });

                                          if (!result.data?.success) {
                                            console.error('Failed to cancel Cloud Task:', result.data?.error);
                                            notificationService.error('Failed to cancel scheduled onboarding');
                                            setIsSendingOnboarding(false);
                                            return;
                                          }
                                        }

                                        const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                        const cohortDoc = await getDoc(cohortRef);

                                        if (cohortDoc.exists()) {
                                          const cohortData = cohortDoc.data();
                                          const actions = cohortData.actions || [];
                                          const presetActions = cohortData.presetActions || [];

                                          const updatedActions = actions.map((action: any) => {
                                            if (action.action === 'welcomePackage' && action.status === 'active') {
                                              return {
                                                ...action,
                                                status: 'terminated',
                                                completedAt: new Date()
                                              };
                                            }
                                            return action;
                                          });

                                          const updatedPresets = presetActions.map((p: any) =>
                                            p.key === 'welcomePackage' && p.status === 'active'
                                              ? { ...p, status: 'pending' }
                                              : p
                                          );

                                          await updateDoc(cohortRef, { actions: updatedActions, presetActions: updatedPresets });

                                          setScheduledOnboardingAction(null);

                                          notificationService.success('Scheduled onboarding email has been cancelled');
                                        }
                                      } catch (error) {
                                        console.error('Error cancelling scheduled onboarding:', error);
                                        notificationService.error('Failed to cancel scheduled onboarding');
                                      } finally {
                                        setIsSendingOnboarding(false);
                                      }
                                    })();
                                  } else {
                                    console.log('✅ Hold complete - scheduling onboarding');

                                    // Schedule onboarding email
                                    (async () => {
                                      if (!selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                                        notificationService.error('Please select a valid cohort');
                                        return;
                                      }

                                      setIsSendingOnboarding(true);
                                      emailGenNotifiedRef.current = null;

                                      // First save onboarding material to cohort, then fire-and-forget
                                      const cohortDocRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                      updateDoc(cohortDocRef, {
                                        customWelcomePackageContent: onboardingMaterial.trim()
                                      }).then(() => {
                                        generateEmails({
                                          tenant_id: currentTenantId,
                                          cohort_id: selectedCohortId,
                                          email_type: 'welcomePackage',
                                          example_email: welcomePackageEmailExample || undefined,
                                        }).then((result) => {
                                          if (result.success) {
                                            notificationService.success(`Onboarding emails scheduled! ${result.data?.successful || 0} emails will be sent in 24 hours.`);
                                          }
                                        }).catch((error: any) => {
                                          // Backend already writes failed status to Firestore; snapshot listener handles it.
                                          console.error('Error scheduling onboarding:', error);
                                          setIsSendingOnboarding(false);
                                          notificationService.error(error.message || 'Failed to schedule onboarding email');
                                        });
                                      }).catch((error: any) => {
                                        console.error('Error saving onboarding material:', error);
                                        notificationService.error('Failed to save onboarding material');
                                        setIsSendingOnboarding(false);
                                      });
                                    })();
                                  }
                                }
                              }, 100);

                              onboardingIntervalRef.current = interval;
                            }}
                            onMouseUp={() => {
                              console.log('🖱️ Mouse up - canceling onboarding hold');
                              if (onboardingIntervalRef.current) {
                                clearInterval(onboardingIntervalRef.current);
                                onboardingIntervalRef.current = null;
                              }
                              setIsHoldingOnboarding(false);
                              setOnboardingHoldProgress(0);
                            }}
                            onMouseLeave={() => {
                              console.log('🖱️ Mouse leave - canceling onboarding hold');
                              if (onboardingIntervalRef.current) {
                                clearInterval(onboardingIntervalRef.current);
                                onboardingIntervalRef.current = null;
                              }
                              setIsHoldingOnboarding(false);
                              setOnboardingHoldProgress(0);
                            }}
                            disabled={
                              isSendingOnboarding ||
                              selectedCohortId === EXAMPLE_COHORT_ID ||
                              scheduledOnboardingAction?.status === 'completed' ||
                              ((cohorts.find(c => c.id === selectedCohortId)?.users?.length || 0) === 0 && scheduledOnboardingAction?.status !== 'active')
                            }
                            className={`relative w-full px-4 py-2 text-sm font-medium text-white ${scheduledOnboardingAction?.status === 'active'
                                ? 'bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600'
                                : 'bg-brand-500 hover:bg-brand-600'
                              } disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2 overflow-hidden`}
                          >
                            {/* Progress bar */}
                            {isHoldingOnboarding && (
                              <div
                                className={`absolute left-0 top-0 h-full ${scheduledOnboardingAction?.status === 'active' ? 'bg-gray-800 dark:bg-gray-700' : 'bg-brand-700'
                                  } transition-all duration-100`}
                                style={{ width: `${onboardingHoldProgress}%` }}
                              ></div>
                            )}

                            <div className="relative z-10 flex items-center gap-2">
                              {isSendingOnboarding ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  <span>Processing...</span>
                                </>
                              ) : (
                                <>
                                  {scheduledOnboardingAction?.status === 'active' ? (
                                    <MdClose className="h-4 w-4" />
                                  ) : (
                                    <MdSend className="h-4 w-4" />
                                  )}
                                  <span>
                                    {isHoldingOnboarding
                                      ? scheduledOnboardingAction?.status === 'active'
                                        ? `Cancelling Scheduled Onboarding (${Math.ceil((100 - onboardingHoldProgress) / 10)}s)`
                                        : `Please review onboarding material before sending (${Math.ceil((100 - onboardingHoldProgress) / 10)}s)`
                                      : scheduledOnboardingAction?.status === 'active'
                                        ? 'Hold to Cancel Scheduled Onboarding'
                                        : 'Hold to Schedule Onboarding Email'}
                                  </span>
                                </>
                              )}
                            </div>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {openStepPopup === 'createMatches' && (
                    <>
                      <LockBanner lockState={cohortLockState} />

                      {/* Description and Instructions */}
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                        <div className="flex items-start gap-2">
                          <MdInfo className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-base text-gray-700 dark:text-gray-200 font-medium mb-3">
                              Run AI matching to create optimal pairings
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              ⚠️ May take up to <strong>10 minutes</strong> depending on cohort size. Please keep this page open.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Existing matches status */}
                      <div className={`mb-4 p-4 rounded-lg border ${
                        pairs.length > 0
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                          : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'
                      }`}>
                        <div className="flex items-start gap-2">
                          <MdAutoGraph className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                            pairs.length > 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-gray-500 dark:text-gray-400'
                          }`} />
                          <div className="flex-1">
                            <h4 className="font-semibold text-navy-700 dark:text-white mb-1">
                              {pairs.length > 0 ? 'Matches Generated' : 'Match Status'}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {pairs.length > 0 ? (
                                <>
                                  <strong>{pairs.length}</strong> match{pairs.length !== 1 ? 'es' : ''} found for this cohort. Review them below.
                                </>
                              ) : (
                                'No matches yet. Click "Generate Matches" below to create AI-powered pairings.'
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={() => setOpenStepPopup(null)}
                          disabled={isGeneratingMatches}
                          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                        >
                          Cancel
                        </button>

                        {pairs.length > 0 && (
                          <button
                            onClick={() => {
                              if (!selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                                return;
                              }
                              if (isCohortLocked) {
                                notificationService.warning(lockTooltip);
                                return;
                              }

                              setConfirmConfig({
                                isOpen: true,
                                message: `Are you sure you want to clear all ${pairs.length} match${pairs.length !== 1 ? 'es' : ''}? This action cannot be undone.`,
                                title: 'Clear Matches',
                                onConfirm: async () => {
                                  try {
                                    // Delete all match documents for this cohort
                                    const matchesRef = collection(db, `tenants/${currentTenantId}/cohorts/${selectedCohortId}/matches`);
                                    const matchesSnapshot = await getDocs(matchesRef);

                                    // Actually delete all match documents
                                    const deletePromises = matchesSnapshot.docs.map(matchDoc =>
                                      deleteDoc(matchDoc.ref)
                                    );
                                    await Promise.all(deletePromises);

                                    // Clear unmatched data from cohort document in Firestore
                                    const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                    await updateDoc(cohortRef, {
                                      unmatchedMentees: [],
                                      unmatchedMentors: [],
                                      recommendations: [],
                                      distributionMetrics: null
                                    });

                                    // Clear pairs in UI
                                    setPairs([]);
                                    setUnmatchedMentees([]);
                                    setUnmatchedMentors([]);
                                    setRecommendations([]);
                                    setDistributionMetrics(null);

                                    notificationService.success(`Deleted ${matchesSnapshot.docs.length} matches successfully`);
                                    console.log(`✅ Deleted ${matchesSnapshot.docs.length} matches from collection`);
                                  } catch (error) {
                                    console.error('Error clearing matches:', error);
                                    notificationService.error('Failed to clear matches');
                                  }
                                },
                              });
                            }}
                            disabled={isGeneratingMatches || selectedCohortId === EXAMPLE_COHORT_ID || isCohortLocked}
                            title={isCohortLocked ? lockTooltip : undefined}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            <MdClose className="h-4 w-4" />
                            Clear Matches
                          </button>
                        )}

                        <button
                          onClick={async () => {
                            if (!selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                              notificationService.error('Please select a valid cohort');
                              return;
                            }
                            if (isCohortLocked) {
                              notificationService.warning(lockTooltip);
                              return;
                            }

                            if (selectedParticipants.length === 0) {
                              notificationService.error('Please select participants first');
                              return;
                            }

                            setIsGeneratingMatches(true);

                            try {
                              // Separate mentors and mentees from selected participants
                              const mentors = selectedParticipants.filter(user => user.profile?.mentor);
                              const mentees = selectedParticipants.filter(user => !user.profile?.mentor);

                              if (mentors.length === 0) {
                                notificationService.error('No mentors found in selected participants');
                                setIsGeneratingMatches(false);
                                return;
                              }

                              if (mentees.length === 0) {
                                notificationService.error('No mentees found in selected participants');
                                setIsGeneratingMatches(false);
                                return;
                              }

                              notificationService.info('Generating matches... This may take a few minutes.');
                              matchingNotifiedRef.current = null;

                              // Don't await — the callable runs for the full duration on the backend.
                              // Progress and completion are tracked via onSnapshot on the cohort doc.
                              createMentorshipMatches({
                                tenant_id: currentTenantId,
                                cohort_id: selectedCohortId
                              }).catch((err: any) => {
                                console.error('createMentorshipMatches callable error:', err);
                                // Only show error if onSnapshot hasn't already handled it
                                if (!matchingNotifiedRef.current) {
                                  notificationService.error('Failed to generate matches');
                                  setIsGeneratingMatches(false);
                                  setMatchingStep(null);
                                  setMatchingStartedAt(null);
                                }
                              });
                            } catch (error) {
                              console.error('Error generating matches:', error);
                              notificationService.error('Failed to generate matches');
                              setIsGeneratingMatches(false);
                              setMatchingStep(null);
                              setMatchingStartedAt(null);
                            }
                          }}
                          disabled={isGeneratingMatches || selectedCohortId === EXAMPLE_COHORT_ID || isCohortLocked}
                          title={isCohortLocked ? lockTooltip : undefined}
                          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          {isGeneratingMatches ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              <span>Generating Matches...</span>
                            </>
                          ) : (
                            <>
                              <MdAutoGraph className="h-4 w-4" />
                              <span>Generate Matches</span>
                            </>
                          )}
                        </button>

                        {/* Progress steps panel */}
                        {isGeneratingMatches && matchingStep && (
                          <div className="p-3 bg-gray-50 dark:bg-navy-800 rounded-lg border border-gray-200 dark:border-navy-600">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Progress</p>
                              {matchingElapsed > 0 && (
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {Math.floor(matchingElapsed / 60)}:{String(matchingElapsed % 60).padStart(2, '0')}
                                </p>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              {[
                                { key: 'validating', label: 'Validating cohort' },
                                { key: 'loading', label: 'Loading profiles' },
                                { key: 'preparing', label: 'Preparing data' },
                                { key: 'matching', label: 'Generating matches (AI)' },
                                { key: 'validating_results', label: 'Validating results' },
                                { key: 'clearing', label: 'Clearing old matches' },
                                { key: 'saving', label: 'Saving matches' },
                                { key: 'complete', label: 'Complete' },
                              ].map((step, i, arr) => {
                                const currentIdx = arr.findIndex(s => s.key === matchingStep);
                                const isDone = i < currentIdx || matchingStep === 'complete';
                                const isCurrent = i === currentIdx && matchingStep !== 'complete';
                                return (
                                  <div key={step.key} className="flex items-center gap-2">
                                    {isDone ? (
                                      <MdCheck className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                                    ) : isCurrent ? (
                                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-brand-500 border-t-transparent flex-shrink-0"></div>
                                    ) : (
                                      <div className="h-3.5 w-3.5 rounded-full border border-gray-300 dark:border-gray-600 flex-shrink-0"></div>
                                    )}
                                    <span className={`text-xs ${isDone ? 'text-green-600 dark:text-green-400' : isCurrent ? 'text-brand-600 dark:text-brand-400 font-medium' : 'text-gray-400 dark:text-gray-500'}`}>
                                      {step.label}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {openStepPopup === 'launch' && (
                    <>
                      {/* Description and What Happens */}
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                        <div className="flex items-start gap-2">
                          <MdInfo className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-3">
                              Send AI-generated connection emails to all matched pairs
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              💡 Emails will be sent <strong>24 hours after launching</strong>. Customized send time coming soon.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* No matches warning */}
                      {pairs.length === 0 && !scheduledSendAction && (
                        <div className="mb-4 p-4 rounded-lg border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
                          <div className="flex items-start gap-2">
                            <MdInfo className="h-5 w-5 flex-shrink-0 mt-0.5 text-yellow-600 dark:text-yellow-400" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-navy-700 dark:text-white mb-1">
                                No Matches Available
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                Please create matches before launching the cohort. Go to the "Create Matches" step to generate mentor-mentee pairings.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <ActionStatusBar
                          action={scheduledSendAction}
                          label="Connection"
                          notScheduledText="No emails currently scheduled. Click the button below to launch and send connection emails."
                        />
                      </div>

                      {/* Example Email Preview */}
                      <div className="mt-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => setIsExampleEmailExpanded(!isExampleEmailExpanded)}
                          className="w-full p-4 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-lg"
                        >
                          <h4 className="font-semibold text-navy-700 dark:text-white">📧 Example Connection Email</h4>
                          {isExampleEmailExpanded ? (
                            <MdExpandLess className="h-5 w-5 text-gray-500" />
                          ) : (
                            <MdExpandMore className="h-5 w-5 text-gray-500" />
                          )}
                        </button>

                        {isExampleEmailExpanded && (
                          <div className="px-4 pb-4">
                            {isLoadingConnectionPreview ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Generating preview live</span>
                              </div>
                            ) : exampleEmail ? (
                              <div className="space-y-2 text-xs">
                                <div><strong>To:</strong> {exampleEmail.recipients?.join(', ')}</div>
                                <div><strong>Subject:</strong> {exampleEmail.subject}</div>
                                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                  <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                                    {exampleEmail.content}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 dark:text-gray-400 py-4">
                                Generating live email preview...
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="mt-6">

                        <div className="relative w-full">
                          <button
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (isGeneratingEmails || !selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                                console.log('⚠️ Cannot start hold - conditions not met:', {
                                  isGeneratingEmails,
                                  selectedCohortId,
                                  currentTenantId,
                                  isExample: selectedCohortId === EXAMPLE_COHORT_ID
                                });
                                return;
                              }

                              // Clear any existing interval first
                              if (launchIntervalRef.current) {
                                clearInterval(launchIntervalRef.current);
                                launchIntervalRef.current = null;
                              }

                              console.log('🖱️ Mouse down - starting hold');
                              setIsHoldingLaunch(true);
                              setLaunchHoldProgress(0);

                              let progress = 0;
                              const interval = setInterval(() => {
                                progress += 1;
                                setLaunchHoldProgress(progress);

                                if (progress >= 100) {
                                  clearInterval(interval);
                                  launchIntervalRef.current = null;
                                  setIsHoldingLaunch(false);
                                  setLaunchHoldProgress(0);

                                  // Check if we're canceling or launching
                                  if (scheduledSendAction?.status === 'active') {
                                    console.log('✅ Hold complete - canceling scheduled send');

                                    // Cancel scheduled send
                                    (async () => {
                                      if (!selectedCohortId || !currentTenantId) return;

                                      setIsGeneratingEmails(true);

                                      try {
                                        // Cancel the Cloud Task if scheduledTaskID exists
                                        if (scheduledSendAction.scheduledTaskID) {
                                          console.log('📧 Cancelling Cloud Task:', scheduledSendAction.scheduledTaskID);

                                                                                    const cancelTask = httpsCallable(functions, 'cancelScheduledTask');

                                          const result: any = await cancelTask({ taskName: scheduledSendAction.scheduledTaskID });

                                          if (!result.data?.success) {
                                            console.error('Failed to cancel Cloud Task:', result.data?.error);
                                            notificationService.error('Failed to cancel scheduled task');
                                            setIsGeneratingEmails(false);
                                            return;
                                          }

                                          console.log('✅ Cloud Task cancelled successfully');
                                        }

                                        // Update Firestore document
                                        const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                        const cohortDoc = await getDoc(cohortRef);

                                        if (cohortDoc.exists()) {
                                          const cohortData = cohortDoc.data();
                                          const actions = cohortData.actions || [];
                                          const presetActions = cohortData.presetActions || [];

                                          // Update the sendMatches action to terminated
                                          const updatedActions = actions.map((action: any) => {
                                            if (action.action === 'sendMatches' && action.status === 'active') {
                                              return {
                                                ...action,
                                                status: 'terminated',
                                                completedAt: new Date()
                                              };
                                            }
                                            return action;
                                          });

                                          const updatedPresets = presetActions.map((p: any) =>
                                            p.key === 'connectionEmail' && p.status === 'active'
                                              ? { ...p, status: 'pending' }
                                              : p
                                          );

                                          await updateDoc(cohortRef, { actions: updatedActions, presetActions: updatedPresets });

                                          // Clear local state
                                          setScheduledSendAction(null);

                                          notificationService.success('Scheduled email send has been cancelled');
                                          console.log('✅ Cancelled scheduled send');
                                        }
                                      } catch (error) {
                                        console.error('Error cancelling scheduled send:', error);
                                        notificationService.error('Failed to cancel scheduled send');
                                      } finally {
                                        setIsGeneratingEmails(false);
                                      }
                                    })();
                                  } else {
                                    console.log('✅ Hold complete - triggering launch');

                                    // Trigger launch
                                    (async () => {
                                      if (!selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                                        notificationService.error('Please select a valid cohort');
                                        return;
                                      }

                                      setIsGeneratingEmails(true);
                                      emailGenNotifiedRef.current = null;

                                      generateEmails({
                                        tenant_id: currentTenantId,
                                        cohort_id: selectedCohortId,
                                        email_type: 'connection',
                                        example_email: exampleEmail || undefined,
                                      }).then((result) => {
                                        if (result.success) {
                                          notificationService.success(`Connection emails scheduled! ${result.data?.successful || 0} emails will be sent in 24 hours.`);
                                        }
                                      }).catch((error: any) => {
                                        // Backend already writes failed status to Firestore; snapshot listener handles it.
                                        // This catch is a fallback for transport-level errors only.
                                        console.error('Error generating connection emails:', error);
                                        setIsGeneratingEmails(false);
                                        notificationService.error(error.message || 'Failed to generate connection emails');
                                      });
                                    })();
                                  }
                                }
                              }, 100);

                              launchIntervalRef.current = interval;
                            }}
                            onMouseUp={() => {
                              console.log('🖱️ Mouse up - canceling hold');
                              if (launchIntervalRef.current) {
                                clearInterval(launchIntervalRef.current);
                                launchIntervalRef.current = null;
                              }
                              setIsHoldingLaunch(false);
                              setLaunchHoldProgress(0);
                            }}
                            onMouseLeave={() => {
                              console.log('🖱️ Mouse leave - canceling hold');
                              if (launchIntervalRef.current) {
                                clearInterval(launchIntervalRef.current);
                                launchIntervalRef.current = null;
                              }
                              setIsHoldingLaunch(false);
                              setLaunchHoldProgress(0);
                            }}
                            disabled={isGeneratingEmails || selectedCohortId === EXAMPLE_COHORT_ID || scheduledSendAction?.status === 'completed' || (pairs.length === 0 && scheduledSendAction?.status !== 'active')}
                            className={`relative w-full px-4 py-2 text-sm font-medium text-white ${
                              scheduledSendAction?.status === 'active'
                                ? 'bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600'
                                : 'bg-brand-500 hover:bg-brand-600'
                            } disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2 overflow-hidden`}
                          >
                            {/* Progress bar */}
                            {isHoldingLaunch && (
                              <div
                                className={`absolute left-0 top-0 h-full ${
                                  scheduledSendAction?.status === 'active' ? 'bg-gray-800 dark:bg-gray-700' : 'bg-brand-700'
                                } transition-all duration-100`}
                                style={{ width: `${launchHoldProgress}%` }}
                              ></div>
                            )}

                            <div className="relative z-10 flex items-center gap-2">
                              {isGeneratingEmails ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  <span>Generating...</span>
                                </>
                              ) : (
                                <>
                                  {scheduledSendAction?.status === 'active' ? (
                                    <MdClose className="h-4 w-4" />
                                  ) : (
                                    <MdRocket className="h-4 w-4" />
                                  )}
                                  <span>
                                    {isHoldingLaunch
                                      ? scheduledSendAction?.status === 'active'
                                        ? `Cancelling Scheduled Emails (${Math.ceil((100 - launchHoldProgress) / 10)}s)`
                                        : `Please review all participants and matches before launching (${Math.ceil((100 - launchHoldProgress) / 10)}s)`
                                      : scheduledSendAction?.status === 'active'
                                        ? 'Hold to Cancel Scheduled Emails'
                                        : 'Hold to Launch Cohort'}
                                  </span>
                                </>
                              )}
                            </div>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {openStepPopup === 'collectFeedback' && (
                    <>
                      {/* Description */}
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                        <div className="flex items-start gap-2">
                          <MdInfo className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-3">
                              Send personalized feedback surveys to all participants
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              💡 Emails will be sent <strong>24 hours after scheduling</strong>. Customized send time coming soon.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* No matches warning */}
                      {pairs.length === 0 && !scheduledFeedbackAction && (
                        <div className="mb-4 p-4 rounded-lg border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
                          <div className="flex items-start gap-2">
                            <MdInfo className="h-5 w-5 flex-shrink-0 mt-0.5 text-yellow-600 dark:text-yellow-400" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-navy-700 dark:text-white mb-1">
                                No Matches Available
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                Please create matches before collecting feedback. Go to the "Create Matches" step to generate mentor-mentee pairings.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <ActionStatusBar
                          action={scheduledFeedbackAction}
                          label="Feedback"
                          notScheduledText="No emails currently scheduled. Click the button below to schedule feedback emails."
                        />
                      </div>

                      {/* Feedback Material Input */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-navy-700 dark:text-white mb-1">
                          Custom Feedback Content <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Add a survey link or custom message. If provided, it will be included in the feedback email along with standard questions.
                        </p>
                        <div className={`relative border border-gray-300 dark:border-gray-600 rounded-lg ${
                          scheduledFeedbackAction
                            ? 'bg-gray-100 dark:bg-gray-900'
                            : 'bg-white dark:bg-navy-800'
                        }`}>
                          <textarea
                            value={feedbackMaterial}
                            onChange={(e) => setFeedbackMaterial(e.target.value)}
                            placeholder="Add a survey link or additional instructions (e.g., https://forms.google.com/your-survey)"
                            rows={3}
                            maxLength={1000}
                            disabled={!!scheduledFeedbackAction}
                            className="w-full px-4 py-2 text-sm bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none disabled:cursor-not-allowed resize-none disabled:text-gray-600 dark:disabled:text-gray-400"
                          />
                          <span className="absolute bottom-12 right-4 text-xs text-gray-400">{feedbackMaterial.length}/1000</span>
                          <div className="px-4 pb-2 flex justify-end border-t border-gray-200 dark:border-gray-700 pt-2">
                            <button
                              onClick={async () => {
                                if (!currentTenantId || !selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID) {
                                  return;
                                }

                                setIsSavingFeedbackMaterial(true);
                                try {
                                  const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                  await updateDoc(cohortRef, {
                                    customFeedbackContent: feedbackMaterial.trim()
                                  });
                                  notificationService.success('Feedback material saved successfully');
                                  console.log('✅ Feedback material saved');

                                  // Custom content changed — wipe both cached roles, then refetch
                                  // the current one so the admin sees the updated preview immediately.
                                  setFeedbackCache({});
                                  setIsLoadingFeedbackPreview(true);
                                  try {
                                    const previewResult = await generateEmails({
                                      tenant_id: currentTenantId,
                                      cohort_id: selectedCohortId,
                                      email_type: 'feedback',
                                      preview: true,
                                      preview_role: previewRole
                                    });
                                    const freshSample =
                                      previewResult.success && previewResult.data?.sample
                                        ? previewResult.data.sample
                                        : null;
                                    setFeedbackCache({ [previewRole]: freshSample });
                                  } catch (previewError) {
                                    console.error('Error regenerating feedback preview:', previewError);
                                    notificationService.error('Failed to regenerate email preview');
                                  } finally {
                                    setIsLoadingFeedbackPreview(false);
                                  }
                                } catch (error) {
                                  console.error('Error saving feedback material:', error);
                                  notificationService.error('Failed to save feedback material');
                                } finally {
                                  setIsSavingFeedbackMaterial(false);
                                }
                              }}
                              disabled={!!scheduledFeedbackAction || isSavingFeedbackMaterial || selectedCohortId === EXAMPLE_COHORT_ID}
                              className="px-4 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              {isSavingFeedbackMaterial ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Example Survey Email Preview */}
                      <div className="mt-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => setIsExampleFeedbackEmailExpanded(!isExampleFeedbackEmailExpanded)}
                          className="w-full p-4 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-lg"
                        >
                          <h4 className="font-semibold text-navy-700 dark:text-white">📧 Example Feedback Email</h4>
                          {isExampleFeedbackEmailExpanded ? (
                            <MdExpandLess className="h-5 w-5 text-gray-500" />
                          ) : (
                            <MdExpandMore className="h-5 w-5 text-gray-500" />
                          )}
                        </button>

                        {isExampleFeedbackEmailExpanded && (
                          <div className="px-4 pb-4">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Preview as:</span>
                              <div className="inline-flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
                                <button
                                  type="button"
                                  onClick={() => setPreviewRole('mentee')}
                                  className={`px-3 py-1 text-xs font-medium transition-colors ${previewRole === 'mentee' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                >
                                  {customLabels.mentee || 'Mentee'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPreviewRole('mentor')}
                                  className={`px-3 py-1 text-xs font-medium transition-colors ${previewRole === 'mentor' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                >
                                  {customLabels.mentor || 'Mentor'}
                                </button>
                              </div>
                            </div>
                            {isLoadingFeedbackPreview ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Generating preview live</span>
                              </div>
                            ) : feedbackEmailExample ? (
                              <div className="space-y-2 text-xs">
                                {feedbackEmailExample.to && (
                                  <div><strong>To:</strong> {feedbackEmailExample.to}</div>
                                )}
                                {feedbackEmailExample.subject && (
                                  <div><strong>Subject:</strong> {feedbackEmailExample.subject}</div>
                                )}
                                {feedbackEmailExample.content && (
                                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                    <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                                      {feedbackEmailExample.content}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 dark:text-gray-400 py-4">
                                No preview available
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="mt-6">
                        <div className="relative w-full">
                          <button
                            data-testid="feedback-hold-button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (isSendingFeedback || !selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                                console.log('⚠️ Cannot start hold - conditions not met');
                                return;
                              }

                              // Clear any existing interval first
                              if (feedbackIntervalRef.current) {
                                clearInterval(feedbackIntervalRef.current);
                                feedbackIntervalRef.current = null;
                              }

                              console.log('🖱️ Mouse down - starting feedback hold');
                              setIsHoldingFeedback(true);
                              setFeedbackHoldProgress(0);

                              let progress = 0;
                              const interval = setInterval(() => {
                                progress += 1;
                                setFeedbackHoldProgress(progress);

                                if (progress >= 100) {
                                  clearInterval(interval);
                                  feedbackIntervalRef.current = null;
                                  setIsHoldingFeedback(false);
                                  setFeedbackHoldProgress(0);

                                  // Check if we're canceling or scheduling
                                  if (scheduledFeedbackAction?.status === 'active') {
                                    console.log('✅ Hold complete - canceling scheduled feedback');

                                    // Cancel scheduled feedback
                                    (async () => {
                                      if (!selectedCohortId || !currentTenantId) return;

                                      setIsSendingFeedback(true);

                                      try {
                                        // Cancel the Cloud Task if scheduledTaskID exists
                                        if (scheduledFeedbackAction.scheduledTaskID) {
                                          console.log('📧 Cancelling Feedback Cloud Task:', scheduledFeedbackAction.scheduledTaskID);

                                                                                    const cancelTask = httpsCallable(functions, 'cancelScheduledTask');

                                          const result: any = await cancelTask({ taskName: scheduledFeedbackAction.scheduledTaskID });

                                          if (!result.data?.success) {
                                            console.error('Failed to cancel Cloud Task:', result.data?.error);
                                            notificationService.error('Failed to cancel scheduled feedback');
                                            setIsSendingFeedback(false);
                                            return;
                                          }

                                          console.log('✅ Feedback Cloud Task cancelled successfully');
                                        }

                                        // Update Firestore document
                                        const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                        const cohortDoc = await getDoc(cohortRef);

                                        if (cohortDoc.exists()) {
                                          const cohortData = cohortDoc.data();
                                          const actions = cohortData.actions || [];
                                          const presetActions = cohortData.presetActions || [];

                                          // Update the collectFeedback action to terminated
                                          const updatedActions = actions.map((action: any) => {
                                            if (action.action === 'collectFeedback' && action.status === 'active') {
                                              return {
                                                ...action,
                                                status: 'terminated',
                                                completedAt: new Date()
                                              };
                                            }
                                            return action;
                                          });

                                          const updatedPresets = presetActions.map((p: any) =>
                                            p.key === 'feedbackRequest' && p.status === 'active'
                                              ? { ...p, status: 'pending' }
                                              : p
                                          );

                                          await updateDoc(cohortRef, { actions: updatedActions, presetActions: updatedPresets });

                                          // Clear local state
                                          setScheduledFeedbackAction(null);

                                          notificationService.success('Scheduled feedback email has been cancelled');
                                          console.log('✅ Cancelled scheduled feedback');
                                        }
                                      } catch (error) {
                                        console.error('Error cancelling scheduled feedback:', error);
                                        notificationService.error('Failed to cancel scheduled feedback');
                                      } finally {
                                        setIsSendingFeedback(false);
                                      }
                                    })();
                                  } else {
                                    console.log('✅ Hold complete - scheduling feedback');

                                    // Schedule feedback email
                                    (async () => {
                                      if (!selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                                        notificationService.error('Please select a valid cohort');
                                        return;
                                      }

                                      setIsSendingFeedback(true);
                                      emailGenNotifiedRef.current = null;

                                      // First save feedback material to cohort, then fire-and-forget
                                      const cohortDocRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                      updateDoc(cohortDocRef, {
                                        customFeedbackContent: feedbackMaterial.trim()
                                      }).then(() => {
                                        generateEmails({
                                          tenant_id: currentTenantId,
                                          cohort_id: selectedCohortId,
                                          email_type: 'feedback',
                                          example_email: feedbackEmailExample || undefined,
                                        }).then((result) => {
                                          if (result.success) {
                                            notificationService.success(`Feedback emails scheduled! ${result.data?.successful || 0} emails will be sent in 24 hours.`);
                                          }
                                        }).catch((error: any) => {
                                          // Backend already writes failed status to Firestore; snapshot listener handles it.
                                          console.error('Error scheduling feedback:', error);
                                          setIsSendingFeedback(false);
                                          notificationService.error(error.message || 'Failed to schedule feedback email');
                                        });
                                      }).catch((error: any) => {
                                        console.error('Error saving feedback material:', error);
                                        notificationService.error('Failed to save feedback material');
                                        setIsSendingFeedback(false);
                                      });
                                    })();
                                  }
                                }
                              }, 100);

                              feedbackIntervalRef.current = interval;
                            }}
                            onMouseUp={() => {
                              console.log('🖱️ Mouse up - canceling feedback hold');
                              if (feedbackIntervalRef.current) {
                                clearInterval(feedbackIntervalRef.current);
                                feedbackIntervalRef.current = null;
                              }
                              setIsHoldingFeedback(false);
                              setFeedbackHoldProgress(0);
                            }}
                            onMouseLeave={() => {
                              console.log('🖱️ Mouse leave - canceling feedback hold');
                              if (feedbackIntervalRef.current) {
                                clearInterval(feedbackIntervalRef.current);
                                feedbackIntervalRef.current = null;
                              }
                              setIsHoldingFeedback(false);
                              setFeedbackHoldProgress(0);
                            }}
                            disabled={isSendingFeedback || selectedCohortId === EXAMPLE_COHORT_ID || scheduledFeedbackAction?.status === 'completed' || (pairs.length === 0 && scheduledFeedbackAction?.status !== 'active')}
                            className={`relative w-full px-4 py-2 text-sm font-medium text-white ${
                              scheduledFeedbackAction?.status === 'active'
                                ? 'bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600'
                                : 'bg-brand-500 hover:bg-brand-600'
                            } disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2 overflow-hidden`}
                          >
                            {/* Progress bar */}
                            {isHoldingFeedback && (
                              <div
                                className={`absolute left-0 top-0 h-full ${
                                  scheduledFeedbackAction?.status === 'active' ? 'bg-gray-800 dark:bg-gray-700' : 'bg-brand-700'
                                } transition-all duration-100`}
                                style={{ width: `${feedbackHoldProgress}%` }}
                              ></div>
                            )}

                            <div className="relative z-10 flex items-center gap-2">
                              {isSendingFeedback ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  <span>Processing...</span>
                                </>
                              ) : (
                                <>
                                  {scheduledFeedbackAction?.status === 'active' ? (
                                    <MdClose className="h-4 w-4" />
                                  ) : (
                                    <MdSend className="h-4 w-4" />
                                  )}
                                  <span>
                                    {isHoldingFeedback
                                      ? scheduledFeedbackAction?.status === 'active'
                                        ? `Cancelling Scheduled Feedback (${Math.ceil((100 - feedbackHoldProgress) / 10)}s)`
                                        : `Please review feedback material before sending (${Math.ceil((100 - feedbackHoldProgress) / 10)}s)`
                                      : scheduledFeedbackAction?.status === 'active'
                                        ? 'Hold to Cancel Scheduled Feedback'
                                        : 'Hold to Schedule Feedback Email'}
                                  </span>
                                </>
                              )}
                            </div>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Check-in Email */}
                  {openStepPopup === 'checkIn' && (
                    <>
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                          <div className="flex items-start gap-2">
                            <MdInfo className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-3">
                                Send friendly reminders to matched participants to connect
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                💡 Emails will be sent <strong>24 hours after scheduling</strong>. Customized send time coming soon.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* No matches warning */}
                        {pairs.length === 0 && !scheduledCheckInAction && (
                          <div className="mb-4 p-4 rounded-lg border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
                            <div className="flex items-start gap-2">
                              <MdInfo className="h-5 w-5 flex-shrink-0 mt-0.5 text-yellow-600 dark:text-yellow-400" />
                              <div className="flex-1">
                                <h4 className="font-semibold text-navy-700 dark:text-white mb-1">
                                  No Matches Available
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                  Please create matches before sending check-in emails. Go to the "Create Matches" step to generate pairings.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        <ActionStatusBar
                          action={scheduledCheckInAction}
                          label="Check-in"
                        />

                        {/* Check-in Material Input */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-navy-700 dark:text-white mb-1">
                            Custom Check-in Content <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">(Optional)</span>
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Add a link or custom message. If provided, it will be included in the check-in email along with the standard check-in content.
                          </p>
                          <div className={`relative border border-gray-300 dark:border-gray-600 rounded-lg ${
                            scheduledCheckInAction
                              ? 'bg-gray-100 dark:bg-gray-900'
                              : 'bg-white dark:bg-navy-800'
                          }`}>
                            <textarea
                              value={checkInMaterial}
                              onChange={(e) => setCheckInMaterial(e.target.value)}
                              placeholder="Add a link or additional instructions (e.g., https://calendly.com/your-link)"
                              rows={3}
                              maxLength={1000}
                              disabled={!!scheduledCheckInAction}
                              className="w-full px-4 py-2 text-sm bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none disabled:cursor-not-allowed resize-none disabled:text-gray-600 dark:disabled:text-gray-400"
                            />
                            <span className="absolute bottom-12 right-4 text-xs text-gray-400">{checkInMaterial.length}/1000</span>
                            <div className="px-4 pb-2 flex justify-end border-t border-gray-200 dark:border-gray-700 pt-2">
                              <button
                                onClick={async () => {
                                  if (!currentTenantId || !selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID) {
                                    return;
                                  }

                                  setIsSavingCheckInMaterial(true);
                                  try {
                                    const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                    await updateDoc(cohortRef, {
                                      customCheckInContent: checkInMaterial.trim()
                                    });
                                    notificationService.success('Check-in material saved successfully');

                                    // Custom content changed — wipe both cached roles, then refetch
                                    // the current one so the admin sees the updated preview immediately.
                                    setCheckInCache({});
                                    setIsLoadingCheckInPreview(true);
                                    try {
                                      const previewResult = await generateEmails({
                                        tenant_id: currentTenantId,
                                        cohort_id: selectedCohortId,
                                        email_type: 'checkIn',
                                        preview: true,
                                        preview_role: previewRole
                                      });
                                      const freshSample =
                                        previewResult.success && previewResult.data?.sample
                                          ? previewResult.data.sample
                                          : null;
                                      setCheckInCache({ [previewRole]: freshSample });
                                    } catch (previewError) {
                                      console.error('Error regenerating check-in preview:', previewError);
                                      notificationService.error('Failed to regenerate email preview');
                                    } finally {
                                      setIsLoadingCheckInPreview(false);
                                    }
                                  } catch (error) {
                                    console.error('Error saving check-in material:', error);
                                    notificationService.error('Failed to save check-in material');
                                  } finally {
                                    setIsSavingCheckInMaterial(false);
                                  }
                                }}
                                disabled={!!scheduledCheckInAction || isSavingCheckInMaterial || selectedCohortId === EXAMPLE_COHORT_ID}
                                className="px-4 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                              >
                                {isSavingCheckInMaterial ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Example Check-in Email Preview */}
                        <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => setIsExampleCheckInEmailExpanded(!isExampleCheckInEmailExpanded)}
                            className="w-full p-4 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-lg"
                          >
                            <h4 className="font-semibold text-navy-700 dark:text-white">📧 Example Check-in Email</h4>
                            {isExampleCheckInEmailExpanded ? (
                              <MdExpandLess className="h-5 w-5 text-gray-500" />
                            ) : (
                              <MdExpandMore className="h-5 w-5 text-gray-500" />
                            )}
                          </button>

                          {isExampleCheckInEmailExpanded && (
                            <div className="px-4 pb-4">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Preview as:</span>
                                <div className="inline-flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
                                  <button
                                    type="button"
                                    onClick={() => setPreviewRole('mentee')}
                                    className={`px-3 py-1 text-xs font-medium transition-colors ${previewRole === 'mentee' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                  >
                                    {customLabels.mentee || 'Mentee'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPreviewRole('mentor')}
                                    className={`px-3 py-1 text-xs font-medium transition-colors ${previewRole === 'mentor' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                  >
                                    {customLabels.mentor || 'Mentor'}
                                  </button>
                                </div>
                              </div>
                              {isLoadingCheckInPreview ? (
                                <div className="flex items-center justify-center py-8">
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Generating preview live</span>
                                </div>
                              ) : checkInEmailExample ? (
                                <div className="space-y-2 text-xs">
                                  {checkInEmailExample.recipients && (
                                    <div><strong>To:</strong> {checkInEmailExample.recipients.join(', ')}</div>
                                  )}
                                  {checkInEmailExample.subject && (
                                    <div><strong>Subject:</strong> {checkInEmailExample.subject}</div>
                                  )}
                                  {checkInEmailExample.content && (
                                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                      <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                                        {checkInEmailExample.content}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 dark:text-gray-400 py-4">
                                  No preview available
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="mt-6">
                          <div className="relative w-full">
                            <button
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (isSendingCheckIn || !selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                                  console.log('Cannot start hold - conditions not met');
                                  return;
                                }

                                if (checkInIntervalRef.current) {
                                  clearInterval(checkInIntervalRef.current);
                                  checkInIntervalRef.current = null;
                                }

                                console.log('Mouse down - starting check-in hold');
                                setIsHoldingCheckIn(true);
                                setCheckInHoldProgress(0);

                                let progress = 0;
                                const interval = setInterval(() => {
                                  progress += 1;
                                  setCheckInHoldProgress(progress);

                                  if (progress >= 100) {
                                    clearInterval(interval);
                                    checkInIntervalRef.current = null;
                                    setIsHoldingCheckIn(false);
                                    setCheckInHoldProgress(0);

                                    if (scheduledCheckInAction?.status === 'active') {
                                      console.log('Hold complete - canceling scheduled check-in');

                                      (async () => {
                                        if (!selectedCohortId || !currentTenantId) return;

                                        setIsSendingCheckIn(true);

                                        try {
                                          if (scheduledCheckInAction.scheduledTaskID) {
                                            console.log('Cancelling Check-in Cloud Task:', scheduledCheckInAction.scheduledTaskID);

                                                                                        const cancelTask = httpsCallable(functions, 'cancelScheduledTask');
                                            const result: any = await cancelTask({ taskName: scheduledCheckInAction.scheduledTaskID });

                                            if (!result.data?.success) {
                                              console.error('Failed to cancel Cloud Task:', result.data?.error);
                                              notificationService.error('Failed to cancel scheduled check-in');
                                              setIsSendingCheckIn(false);
                                              return;
                                            }

                                            console.log('Check-in Cloud Task cancelled successfully');
                                          }

                                          const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts`, selectedCohortId);
                                          const cohortDoc = await getDoc(cohortRef);

                                          if (cohortDoc.exists()) {
                                            const cohortData = cohortDoc.data();
                                            const actions = cohortData.actions || [];
                                            const presetActions = cohortData.presetActions || [];

                                            const updatedActions = actions.map((action: any) => {
                                              if (action.action === 'sendCheckIn' && action.status === 'active') {
                                                return {
                                                  ...action,
                                                  status: 'terminated',
                                                  completedAt: new Date()
                                                };
                                              }
                                              return action;
                                            });

                                            const updatedPresets = presetActions.map((p: any) =>
                                              p.key === 'checkInEmail' && p.status === 'active'
                                                ? { ...p, status: 'pending' }
                                                : p
                                            );

                                            await updateDoc(cohortRef, { actions: updatedActions, presetActions: updatedPresets });

                                            setScheduledCheckInAction(null);

                                            notificationService.success('Scheduled check-in email has been cancelled');
                                            console.log('Cancelled scheduled check-in');
                                          }
                                        } catch (error) {
                                          console.error('Error cancelling scheduled check-in:', error);
                                          notificationService.error('Failed to cancel scheduled check-in');
                                        } finally {
                                          setIsSendingCheckIn(false);
                                        }
                                      })();
                                    } else {
                                      console.log('Hold complete - scheduling check-in');

                                      (async () => {
                                        if (!selectedCohortId || selectedCohortId === EXAMPLE_COHORT_ID || !currentTenantId) {
                                          notificationService.error('Please select a valid cohort');
                                          return;
                                        }

                                        setIsSendingCheckIn(true);
                                        emailGenNotifiedRef.current = null;

                                        generateEmails({
                                          tenant_id: currentTenantId,
                                          cohort_id: selectedCohortId,
                                          email_type: 'checkIn',
                                          example_email: checkInEmailExample || undefined,
                                        }).then((result) => {
                                          if (result.success) {
                                            notificationService.success(`Check-in emails scheduled! ${result.data?.successful || 0} emails will be sent in 24 hours.`);
                                          }
                                        }).catch((error: any) => {
                                          // Backend already writes failed status to Firestore; snapshot listener handles it.
                                          console.error('Error scheduling check-in:', error);
                                          setIsSendingCheckIn(false);
                                          notificationService.error(error.message || 'Failed to schedule check-in email');
                                        });
                                      })();
                                    }
                                  }
                                }, 100);

                                checkInIntervalRef.current = interval;
                              }}
                              onMouseUp={() => {
                                console.log('Mouse up - canceling check-in hold');
                                if (checkInIntervalRef.current) {
                                  clearInterval(checkInIntervalRef.current);
                                  checkInIntervalRef.current = null;
                                }
                                setIsHoldingCheckIn(false);
                                setCheckInHoldProgress(0);
                              }}
                              onMouseLeave={() => {
                                console.log('Mouse leave - canceling check-in hold');
                                if (checkInIntervalRef.current) {
                                  clearInterval(checkInIntervalRef.current);
                                  checkInIntervalRef.current = null;
                                }
                                setIsHoldingCheckIn(false);
                                setCheckInHoldProgress(0);
                              }}
                              disabled={isSendingCheckIn || selectedCohortId === EXAMPLE_COHORT_ID || scheduledCheckInAction?.status === 'completed' || (pairs.length === 0 && scheduledCheckInAction?.status !== 'active')}
                              className={`relative w-full px-4 py-2 text-sm font-medium text-white ${
                                scheduledCheckInAction?.status === 'active'
                                  ? 'bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600'
                                  : 'bg-brand-500 hover:bg-brand-600'
                              } disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2 overflow-hidden`}
                            >
                              {isHoldingCheckIn && (
                                <div
                                  className={`absolute left-0 top-0 h-full ${
                                    scheduledCheckInAction?.status === 'active' ? 'bg-gray-800 dark:bg-gray-700' : 'bg-brand-700'
                                  } transition-all duration-100`}
                                  style={{ width: `${checkInHoldProgress}%` }}
                                ></div>
                              )}

                              <div className="relative z-10 flex items-center gap-2">
                                {isSendingCheckIn ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    <span>Processing...</span>
                                  </>
                                ) : (
                                  <>
                                    {scheduledCheckInAction?.status === 'active' ? (
                                      <MdClose className="h-4 w-4" />
                                    ) : (
                                      <MdSend className="h-4 w-4" />
                                    )}
                                    <span>
                                      {isHoldingCheckIn
                                        ? scheduledCheckInAction?.status === 'active'
                                          ? `Cancelling Scheduled Check-in (${Math.ceil((100 - checkInHoldProgress) / 10)}s)`
                                          : `Please review before sending (${Math.ceil((100 - checkInHoldProgress) / 10)}s)`
                                        : scheduledCheckInAction?.status === 'active'
                                          ? 'Hold to Cancel Scheduled Check-in'
                                          : 'Hold to Schedule Check-in Email'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Introduce & Connect (Double Opt-In) */}
                  {openStepPopup === 'introduceAndConnect' && (
                    <>
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                          <div className="flex items-start gap-2">
                            <MdInfo className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-3">
                                Send consent emails to both participants in each match
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                💡 Emails will be sent <strong>24 hours after scheduling</strong>. Connection emails sent once both opt in.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* No matches warning */}
                        {pairs.length === 0 && !scheduledConsentAction && (
                          <div className="mb-4 p-4 rounded-lg border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
                            <div className="flex items-start gap-2">
                              <MdInfo className="h-5 w-5 flex-shrink-0 mt-0.5 text-yellow-600 dark:text-yellow-400" />
                              <div className="flex-1">
                                <h4 className="font-semibold text-navy-700 dark:text-white mb-1">
                                  No Matches Available
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                  Please create matches before sending introduction emails. Go to the "Create Matches" step to generate pairings.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        <ActionStatusBar
                          action={scheduledConsentAction}
                          label="Consent"
                        />

                        {/* Example Consent Email Preview */}
                        <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => setIsConsentEmailExpanded(!isConsentEmailExpanded)}
                            className="w-full p-4 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-lg"
                          >
                            <h4 className="font-semibold text-navy-700 dark:text-white">📧 Example Consent Email</h4>
                            {isConsentEmailExpanded ? (
                              <MdExpandLess className="h-5 w-5 text-gray-500" />
                            ) : (
                              <MdExpandMore className="h-5 w-5 text-gray-500" />
                            )}
                          </button>

                          {isConsentEmailExpanded && (
                            <div className="px-4 pb-4">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Preview as:</span>
                                <div className="inline-flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
                                  <button
                                    type="button"
                                    onClick={() => setPreviewRole('mentee')}
                                    className={`px-3 py-1 text-xs font-medium transition-colors ${previewRole === 'mentee' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                  >
                                    {customLabels.mentee || 'Mentee'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPreviewRole('mentor')}
                                    className={`px-3 py-1 text-xs font-medium transition-colors ${previewRole === 'mentor' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                  >
                                    {customLabels.mentor || 'Mentor'}
                                  </button>
                                </div>
                              </div>
                              {isLoadingConsentPreview ? (
                                <div className="flex items-center justify-center py-8">
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Generating preview live</span>
                                </div>
                              ) : consentEmailExample ? (
                                <div className="space-y-2 text-xs">
                                  <div><strong>To:</strong> {consentEmailExample.recipients?.join(', ') || consentEmailExample.to}</div>
                                  <div><strong>Subject:</strong> {consentEmailExample.subject}</div>
                                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                    <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                                      {consentEmailExample.content}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 dark:text-gray-400 py-4">
                                  No preview available
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Action Button */}
                        <div className="mt-6">
                          <div className="relative w-full">
                            <button
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (isGeneratingConsentEmails || selectedCohortId === EXAMPLE_COHORT_ID || scheduledConsentAction?.status === 'completed' || (pairs.length === 0 && scheduledConsentAction?.status !== 'active')) {
                                  return;
                                }

                                if (consentIntervalRef.current) {
                                  clearInterval(consentIntervalRef.current);
                                  consentIntervalRef.current = null;
                                }

                                setIsHoldingConsent(true);
                                setConsentHoldProgress(0);

                                let progress = 0;
                                const interval = setInterval(() => {
                                  progress += 1;
                                  setConsentHoldProgress(progress);

                                  if (progress >= 100) {
                                    clearInterval(interval);
                                    consentIntervalRef.current = null;
                                    setIsHoldingConsent(false);
                                    setConsentHoldProgress(0);

                                    // Check if we're canceling or sending
                                    if (scheduledConsentAction?.status === 'active') {
                                      console.log('✅ Hold complete - canceling scheduled consent emails');
                                      setIsGeneratingConsentEmails(true);

                                      cancelEmails({
                                        tenant_id: currentTenantId!,
                                        cohort_id: selectedCohortId!,
                                        email_type: 'introConsent'
                                      }).then(async (result) => {
                                        if (result.success) {
                                          notificationService.success('Consent emails cancelled successfully!');

                                          // Update actions and presetActions in Firestore
                                          const cohortRef = doc(db, `tenants/${currentTenantId}/cohorts/${selectedCohortId}`);
                                          const cohortDoc = await getDoc(cohortRef);
                                          if (cohortDoc.exists()) {
                                            const cohortData = cohortDoc.data();
                                            const actions = cohortData.actions || [];
                                            const presetActions = cohortData.presetActions || [];

                                            const updatedActions = actions.map((action: any) => {
                                              if (action.action === 'sendIntroConsent' && action.status === 'active') {
                                                return {
                                                  ...action,
                                                  status: 'terminated',
                                                  terminatedAt: new Date(),
                                                };
                                              }
                                              return action;
                                            });

                                            const updatedPresets = presetActions.map((p: any) =>
                                              p.key === 'introduceAndConnect' && p.status === 'active'
                                                ? { ...p, status: 'pending' }
                                                : p
                                            );

                                            await updateDoc(cohortRef, { actions: updatedActions, presetActions: updatedPresets });
                                          }

                                          setScheduledConsentAction(null);
                                        } else {
                                          notificationService.error(result.error || 'Failed to cancel consent emails');
                                        }
                                      }).catch((error) => {
                                        console.error('Error cancelling consent emails:', error);
                                        notificationService.error('Failed to cancel consent emails');
                                      }).finally(() => {
                                        setIsGeneratingConsentEmails(false);
                                      });
                                    } else {
                                      console.log('✅ Hold complete - generating consent emails');
                                      setIsGeneratingConsentEmails(true);
                                      emailGenNotifiedRef.current = null;

                                      generateEmails({
                                        tenant_id: currentTenantId!,
                                        cohort_id: selectedCohortId!,
                                        email_type: 'introConsent',
                                        example_email: consentEmailExample || undefined,
                                      }).then((result) => {
                                        if (result.success) {
                                          notificationService.success(`Consent emails scheduled! ${result.data?.successful || 0} emails will be sent in 24 hours.`);
                                        }
                                      }).catch((error: any) => {
                                        // Backend already writes failed status to Firestore; snapshot listener handles it.
                                        console.error('Error generating consent emails:', error);
                                        setIsGeneratingConsentEmails(false);
                                        notificationService.error(error.message || 'Failed to generate consent emails');
                                      });
                                    }
                                  }
                                }, 100);
                                consentIntervalRef.current = interval;
                              }}
                              onMouseUp={() => {
                                if (consentIntervalRef.current) {
                                  clearInterval(consentIntervalRef.current);
                                  consentIntervalRef.current = null;
                                }
                                setIsHoldingConsent(false);
                                setConsentHoldProgress(0);
                              }}
                              onMouseLeave={() => {
                                if (consentIntervalRef.current) {
                                  clearInterval(consentIntervalRef.current);
                                  consentIntervalRef.current = null;
                                }
                                setIsHoldingConsent(false);
                                setConsentHoldProgress(0);
                              }}
                              disabled={isGeneratingConsentEmails || selectedCohortId === EXAMPLE_COHORT_ID || scheduledConsentAction?.status === 'completed' || (pairs.length === 0 && scheduledConsentAction?.status !== 'active')}
                              className={`relative w-full px-4 py-2 text-sm font-medium text-white ${
                                scheduledConsentAction?.status === 'active'
                                  ? 'bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600'
                                  : 'bg-brand-500 hover:bg-brand-600'
                              } disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2 overflow-hidden`}
                            >
                              {/* Progress bar */}
                              {isHoldingConsent && (
                                <div
                                  className={`absolute left-0 top-0 h-full ${
                                    scheduledConsentAction?.status === 'active' ? 'bg-gray-800 dark:bg-gray-700' : 'bg-brand-700'
                                  } transition-all duration-100`}
                                  style={{ width: `${consentHoldProgress}%` }}
                                ></div>
                              )}

                              <div className="relative z-10 flex items-center gap-2">
                                {isGeneratingConsentEmails ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    <span>Generating Consent Emails...</span>
                                  </>
                                ) : (
                                  <>
                                    {scheduledConsentAction?.status === 'active' ? (
                                      <MdClose className="h-4 w-4" />
                                    ) : (
                                      <MdSend className="h-4 w-4" />
                                    )}
                                    <span>
                                      {isHoldingConsent
                                        ? scheduledConsentAction?.status === 'active'
                                          ? `Cancelling Scheduled Emails (${Math.ceil((100 - consentHoldProgress) / 10)}s)`
                                          : `Sending consent emails to ${pairs.length * 2} participants (${Math.ceil((100 - consentHoldProgress) / 10)}s)`
                                        : scheduledConsentAction?.status === 'active'
                                          ? 'Hold to Cancel Scheduled Emails'
                                          : 'Hold to Send Consent Emails'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Manual Match */}
                  {openStepPopup === 'manualMatch' && (
                    <>
                      <LockBanner lockState={cohortLockState} />
                      <div className="space-y-4">
                        {/* Search Bar */}
                        <div className="relative">
                          <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search participants..."
                            value={manualMatchMentorSearch}
                            onChange={(e) => {
                              setManualMatchMentorSearch(e.target.value);
                              setManualMatchMenteeSearch(e.target.value);
                            }}
                            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                          />
                        </div>

                        {/* Three Column Layout */}
                        <div className="grid grid-cols-3 gap-4">
                          {/* Mentor Column */}
                          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 dark:bg-navy-900 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                              <h4 className="text-sm font-semibold text-navy-700 dark:text-white">
                                {getLabel('mentor', 'Mentors')} ({selectedParticipants.filter(u => u.profile?.mentor === true).length})
                              </h4>
                            </div>
                            <div className="overflow-y-auto max-h-72 p-2 space-y-1">
                              {selectedParticipants
                                .filter(user => user.profile?.mentor === true)
                                .filter(user => {
                                  if (!manualMatchMentorSearch) return true;
                                  const search = manualMatchMentorSearch.toLowerCase();
                                  return user.displayName.toLowerCase().includes(search) ||
                                    user.profile?.email?.toLowerCase().includes(search) ||
                                    user.authEmail?.toLowerCase().includes(search);
                                })
                                .map((user) => (
                                  <div
                                    key={user.uid}
                                    onClick={() => setManualMatchMentor(manualMatchMentor?.uid === user.uid ? null : user)}
                                    className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors cursor-pointer ${
                                      manualMatchMentor?.uid === user.uid
                                        ? 'bg-brand-50 dark:bg-brand-900/30 border border-brand-500 dark:border-brand-400'
                                        : 'bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-700 hover:border-brand-500 dark:hover:border-brand-400'
                                    }`}
                                  >
                                    <Avatar uid={user.uid} displayName={user.displayName} size="small" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-navy-700 dark:text-white truncate">
                                        {user.displayName}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {user.profile?.works?.[0]?.position || user.profile?.email || user.authEmail}
                                      </p>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setViewingUser(user);
                                        setIsProfileModalOpen(true);
                                      }}
                                      className="p-1 hover:bg-gray-100 dark:hover:bg-navy-700 rounded transition-colors flex-shrink-0"
                                      title="View profile"
                                    >
                                      <MdVisibility className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                    </button>
                                    {manualMatchMentor?.uid === user.uid && (
                                      <MdCheck className="h-4 w-4 text-brand-500 flex-shrink-0" />
                                    )}
                                  </div>
                                ))}
                              {selectedParticipants.filter(u => u.profile?.mentor === true).length === 0 && (
                                <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                                  No {getLabel('mentor', 'mentor').toLowerCase()}s
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Mentee Column */}
                          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 dark:bg-navy-900 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                              <h4 className="text-sm font-semibold text-navy-700 dark:text-white">
                                {getLabel('mentee', 'Mentees')} ({selectedParticipants.filter(u => u.profile?.mentor !== true).length})
                              </h4>
                            </div>
                            <div className="overflow-y-auto max-h-72 p-2 space-y-1">
                              {selectedParticipants
                                .filter(user => user.profile?.mentor !== true)
                                .filter(user => {
                                  if (!manualMatchMenteeSearch) return true;
                                  const search = manualMatchMenteeSearch.toLowerCase();
                                  return user.displayName.toLowerCase().includes(search) ||
                                    user.profile?.email?.toLowerCase().includes(search) ||
                                    user.authEmail?.toLowerCase().includes(search);
                                })
                                .map((user) => (
                                  <div
                                    key={user.uid}
                                    onClick={() => setManualMatchMentee(manualMatchMentee?.uid === user.uid ? null : user)}
                                    className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors cursor-pointer ${
                                      manualMatchMentee?.uid === user.uid
                                        ? 'bg-brand-50 dark:bg-brand-900/30 border border-brand-500 dark:border-brand-400'
                                        : 'bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-700 hover:border-brand-500 dark:hover:border-brand-400'
                                    }`}
                                  >
                                    <Avatar uid={user.uid} displayName={user.displayName} size="small" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-navy-700 dark:text-white truncate">
                                        {user.displayName}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {user.profile?.works?.[0]?.position || user.profile?.email || user.authEmail}
                                      </p>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setViewingUser(user);
                                        setIsProfileModalOpen(true);
                                      }}
                                      className="p-1 hover:bg-gray-100 dark:hover:bg-navy-700 rounded transition-colors flex-shrink-0"
                                      title="View profile"
                                    >
                                      <MdVisibility className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                    </button>
                                    {manualMatchMentee?.uid === user.uid && (
                                      <MdCheck className="h-4 w-4 text-brand-500 flex-shrink-0" />
                                    )}
                                  </div>
                                ))}
                              {selectedParticipants.filter(u => u.profile?.mentor !== true).length === 0 && (
                                <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                                  No {getLabel('mentee', 'mentee').toLowerCase()}s
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Match Preview & Reasons Column */}
                          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 dark:bg-navy-900 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                              <h4 className="text-sm font-semibold text-navy-700 dark:text-white">
                                New Match
                              </h4>
                            </div>
                            <div className="p-3 space-y-3">
                              {/* Selected Match Preview */}
                              <div className={`rounded-lg p-3 border ${
                                manualMatchMentor && manualMatchMentee
                                  ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-700'
                                  : 'bg-gray-50 dark:bg-navy-900 border-gray-200 dark:border-gray-700 border-dashed'
                              }`}>
                                {manualMatchMentor && manualMatchMentee ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-5 h-5 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                                      <MdPerson className="h-3 w-3 text-green-600 dark:text-green-400" />
                                    </div>
                                    <span className="text-sm font-medium text-navy-700 dark:text-white truncate">
                                      {manualMatchMentor.displayName.split(' ')[0]}
                                    </span>
                                    <span className="text-gray-400 text-xs flex-shrink-0">→</span>
                                    <div className="w-5 h-5 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                                      <MdPerson className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <span className="text-sm font-medium text-navy-700 dark:text-white truncate">
                                      {manualMatchMentee.displayName.split(' ')[0]}
                                    </span>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-400 text-center">
                                    Select a {getLabel('mentor', 'mentor').toLowerCase()} and {getLabel('mentee', 'mentee').toLowerCase()}
                                  </p>
                                )}
                              </div>

                              {/* Match Reasons */}
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                  Match Reasons (required)
                                </label>
                                {manualMatchReasons.length > 0 && (
                                  <ul className="space-y-1 mb-2">
                                    {manualMatchReasons.map((reason, index) => (
                                      <li key={index} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-navy-900 px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700">
                                        <span className="text-brand-500">•</span>
                                        <span className="flex-1 truncate">{reason}</span>
                                        <button
                                          onClick={() => setManualMatchReasons(manualMatchReasons.filter((_, i) => i !== index))}
                                          className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                        >
                                          <MdClose className="h-3 w-3" />
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                <div className="flex gap-1">
                                  <input
                                    type="text"
                                    value={manualMatchReasonInput}
                                    onChange={(e) => setManualMatchReasonInput(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && manualMatchReasonInput.trim()) {
                                        e.preventDefault();
                                        setManualMatchReasons([...manualMatchReasons, manualMatchReasonInput.trim()]);
                                        setManualMatchReasonInput('');
                                      }
                                    }}
                                    placeholder="Add a reason..."
                                    className="flex-1 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                  />
                                  <button
                                    onClick={() => {
                                      if (manualMatchReasonInput.trim()) {
                                        setManualMatchReasons([...manualMatchReasons, manualMatchReasonInput.trim()]);
                                        setManualMatchReasonInput('');
                                      }
                                    }}
                                    disabled={!manualMatchReasonInput.trim()}
                                    className="px-2 py-1.5 text-xs font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/30 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => setOpenStepPopup(null)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleCreateManualMatch}
                            disabled={!manualMatchMentor || !manualMatchMentee || manualMatchReasons.length === 0 || isCreatingManualMatch || selectedCohortId === EXAMPLE_COHORT_ID || isCohortLocked}
                            title={isCohortLocked ? lockTooltip : undefined}
                            className="px-4 py-2 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                          >
                            {isCreatingManualMatch ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                <span>Creating...</span>
                              </>
                            ) : (
                              <span>Create Match</span>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Footer */}
                {openStepPopup !== 'launch' && openStepPopup !== 'onboarding' && openStepPopup !== 'createMatches' && openStepPopup !== 'selectParticipants' && openStepPopup !== 'collectFeedback' && openStepPopup !== 'introduceAndConnect' && openStepPopup !== 'manualMatch' && (
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setOpenStepPopup(null)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Conflict Info Popup */}
      {showConflictInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-3">
              Existing Matches Found
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              The selected users already have existing matches. You can still create this new match.
            </p>

            {/* Show existing matches */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 mb-4 border border-yellow-200 dark:border-yellow-700">
              <p className="text-xs font-medium text-yellow-700 dark:text-yellow-400 mb-2">Existing matches:</p>
              <div className="space-y-2">
                {conflictingMatches.slice(0, 4).map((match) => (
                  <div key={match.id} className="flex items-center gap-2 text-sm">
                    <div className="w-5 h-5 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <MdPerson className="h-3 w-3 text-green-600 dark:text-green-400" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{match.mentor.firstName}</span>
                    <span className="text-gray-400">→</span>
                    <div className="w-5 h-5 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <MdPerson className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{match.mentee.firstName}</span>
                  </div>
                ))}
                {conflictingMatches.length > 4 && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400">
                    +{conflictingMatches.length - 4} more existing match(es)
                  </p>
                )}
              </div>
            </div>

            {/* New match preview */}
            <div className="bg-brand-50 dark:bg-brand-900/20 rounded-lg p-3 mb-4 border border-brand-200 dark:border-brand-700">
              <p className="text-xs font-medium text-brand-600 dark:text-brand-400 mb-2">New match to create:</p>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-5 h-5 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <MdPerson className="h-3 w-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-navy-700 dark:text-white font-medium">{manualMatchMentor?.displayName}</span>
                <span className="text-gray-400">→</span>
                <div className="w-5 h-5 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <MdPerson className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-navy-700 dark:text-white font-medium">{manualMatchMentee?.displayName}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConflictInfo(false);
                  setConflictingMatches([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConflictInfo(false);
                  executeCreateManualMatch();
                }}
                disabled={isCreatingManualMatch}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 rounded-lg transition-colors flex items-center gap-2"
              >
                {isCreatingManualMatch ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <span>Create Anyway</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">

        {/* Left Column: Matches */}
        <div className="space-y-6 xl:col-span-3">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-semibold text-navy-700 dark:text-white">
                Matches
              </h2>
              <div className="flex items-center gap-3">
                <div className="bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-lg border border-blue-200 dark:border-blue-800">
                  <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{pendingPairs.length}</span>
                  <span className="text-xs text-blue-600 dark:text-blue-400 ml-1">Matched</span>
                </div>
                {displayUnmatchedMentees.length > 0 && (
                  <button
                    onClick={() => {
                      unmatchedMenteesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="bg-gray-50 dark:bg-gray-900/20 px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 cursor-pointer"
                    title={`Jump to unmatched ${getLabel('mentee', 'mentees').toLowerCase()}`}
                  >
                    <div>
                      <span className="text-lg font-bold text-gray-600 dark:text-gray-400">{displayUnmatchedMentees.length}</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400 ml-1">Unmatched {getLabel('mentee', 'Mentee')}{displayUnmatchedMentees.length !== 1 ? 's' : ''}</span>
                    </div>
                    <MdKeyboardArrowDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
                {displayUnmatchedMentors.length > 0 && (
                  <button
                    onClick={() => {
                      unmatchedMentorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="bg-gray-50 dark:bg-gray-900/20 px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 cursor-pointer"
                    title={`Jump to unmatched ${getLabel('mentor', 'mentors').toLowerCase()}`}
                  >
                    <div>
                      <span className="text-lg font-bold text-gray-600 dark:text-gray-400">{displayUnmatchedMentors.length}</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400 ml-1">Unmatched {getLabel('mentor', 'Mentor')}{displayUnmatchedMentors.length !== 1 ? 's' : ''}</span>
                    </div>
                    <MdKeyboardArrowDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            {/* Search Bar and Preferences Button */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search matches..."
                  value={recommendedSearch}
                  onChange={(e) => setRecommendedSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* Manual Add Match Button */}
              {selectedCohortId && selectedCohortId !== EXAMPLE_COHORT_ID && (
                <button
                  onClick={() => {
                    if (isCohortLocked) {
                      notificationService.warning(lockTooltip);
                      return;
                    }
                    setManualMatchMentor(null);
                    setManualMatchMentee(null);
                    setManualMatchReasons([]);
                    setManualMatchReasonInput('');
                    setManualMatchMentorSearch('');
                    setManualMatchMenteeSearch('');
                    setOpenStepPopup('manualMatch');
                  }}
                  disabled={isCohortLocked}
                  className="px-4 py-2 rounded-lg border transition-all flex items-center justify-center gap-2 bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800 hover:bg-brand-100 dark:hover:bg-brand-800/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-50 dark:disabled:hover:bg-brand-900/20 whitespace-nowrap"
                  title={isCohortLocked ? lockTooltip : 'Manually add a match'}
                >
                  <MdAdd className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                  <span className="text-sm font-medium text-brand-600 dark:text-brand-400">Add Match</span>
                </button>
              )}

              {/* Preferences Button */}
              <button
                onClick={() => setShowCustomAttributes(!showCustomAttributes)}
                className="px-4 py-2 rounded-lg border transition-all flex items-center justify-center gap-2 bg-gray-50 dark:bg-navy-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-navy-700 hover:border-gray-300 dark:hover:border-gray-600 whitespace-nowrap"
                title="Configure preferences"
              >
                <MdSettings className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Preferences
                </span>
              </button>
            </div>

            {/* Preferences Input Fields */}
            {showCustomAttributes && (
              <div className="p-4 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                <h4 className="font-medium text-navy-700 dark:text-white text-sm mb-3">
                  Custom Labels
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Personalize terminology to match your program.
                </p>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Mentor Label
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Advisor, Coach, Guide"
                    value={customLabels.mentor}
                    onChange={(e) => setCustomLabels({ ...customLabels, mentor: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Mentee Label
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Learner, Protégé, Member"
                    value={customLabels.mentee}
                    onChange={(e) => setCustomLabels({ ...customLabels, mentee: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Connection Label
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Partnership, Relationship, Connection"
                    value={customLabels.mentorship}
                    onChange={(e) => setCustomLabels({ ...customLabels, mentorship: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max {getLabel('mentee', 'Mentee')}s per {getLabel('mentor', 'Mentor')}
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={maxMenteesPerMentor}
                      onChange={(e) => setMaxMenteesPerMentor(Number(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-brand-500"
                    />
                    <div className="w-12 px-2 py-1 text-center text-sm font-medium bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 rounded border border-brand-200 dark:border-brand-700">
                      {maxMenteesPerMentor}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300 mt-2 font-medium">
                    <span>Maximize {getLabel('mentor', 'mentor').toLowerCase()} utilization</span>
                    <span>Optimize match quality</span>
                  </div>
                </div>

                <button
                  onClick={async () => {
                    if (selectedCohortId === EXAMPLE_COHORT_ID) {
                      notificationService.warning('Cannot save settings for the example cohort. Please create a new cohort first.');
                      return;
                    }

                    if (!currentTenantId || !selectedCohortId) {
                      return;
                    }

                    if (!customLabels.mentor?.trim() || !customLabels.mentee?.trim() || !customLabels.mentorship?.trim()) {
                      notificationService.error('All labels must be filled out');
                      return;
                    }

                    try {
                      const labelsToSave = {
                        mentor: customLabels.mentor.trim(),
                        mentee: customLabels.mentee.trim(),
                        mentorship: customLabels.mentorship.trim()
                      };

                      const updateData: any = {
                        customLabels: labelsToSave,
                        customMentorMenteeRatio: maxMenteesPerMentor
                      };

                      await updateDoc(doc(db, 'tenants', currentTenantId, 'cohorts', selectedCohortId), updateData);

                      // Update local state
                      setCohorts(prevCohorts =>
                        prevCohorts.map(cohort =>
                          cohort.id === selectedCohortId
                            ? {
                                ...cohort,
                                customLabels: labelsToSave,
                                customMentorMenteeRatio: maxMenteesPerMentor
                              }
                            : cohort
                        )
                      );

                      notificationService.success('Preferences saved successfully!');
                      console.log('✅ Preferences saved:', updateData);
                    } catch (error) {
                      console.error('❌ Error saving labels:', error);
                      notificationService.error('Failed to save labels. Please try again.');
                    }
                  }}
                  disabled={selectedCohortId === EXAMPLE_COHORT_ID}
                  className="w-full px-3 py-2 mt-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Preferences
                </button>
              </div>
            )}
          </div>

          {filteredPendingPairs.length > 0 ? (
            <div className="space-y-4">
              {groupedByMentor.map((group) => (
                <div key={group.mentor.uid} className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
                  {/* Mentor header */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border-b border-gray-200 dark:border-gray-700">
                    <div className="w-5 h-5 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <MdPerson className="h-3 w-3 text-green-600 dark:text-green-400" />
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          if (selectedCohortId === EXAMPLE_COHORT_ID) {
                            const enhancedUser = convertUserToEnhancedUser(group.mentor);
                            enhancedUser.profile!.mentor = true;
                            setViewingUser(enhancedUser);
                            setIsProfileModalOpen(true);
                          } else {
                            const user = await userDataService.fetchSingleUser(group.mentor.uid);
                            if (user) {
                              setViewingUser(user);
                              setIsProfileModalOpen(true);
                            }
                          }
                        } catch (error) {
                          console.error('Error loading user profile:', error);
                          notificationService.error('Failed to load profile');
                        }
                      }}
                      className="font-medium text-navy-700 dark:text-white text-sm hover:text-green-600 dark:hover:text-green-400 hover:underline transition-colors"
                      title="View mentor profile"
                    >
                      {group.mentor.firstName} {group.mentor.lastName}
                    </button>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({group.pairs.length} {group.pairs.length === 1 ? customLabels.mentee?.toLowerCase() || 'mentee' : (customLabels.mentee?.toLowerCase() || 'mentee') + 's'})
                    </span>
                  </div>

                  {/* Mentee rows */}
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {group.pairs.map((pair) => (
                      <div key={pair.id} className="px-3 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="w-5 h-5 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0 ml-6">
                              <MdPerson className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  if (selectedCohortId === EXAMPLE_COHORT_ID) {
                                    const enhancedUser = convertUserToEnhancedUser(pair.mentee);
                                    enhancedUser.profile!.mentor = false;
                                    setViewingUser(enhancedUser);
                                    setIsProfileModalOpen(true);
                                  } else {
                                    const user = await userDataService.fetchSingleUser(pair.mentee.uid);
                                    if (user) {
                                      setViewingUser(user);
                                      setIsProfileModalOpen(true);
                                    }
                                  }
                                } catch (error) {
                                  console.error('Error loading user profile:', error);
                                  notificationService.error('Failed to load profile');
                                }
                              }}
                              className="font-medium text-navy-700 dark:text-white text-xs truncate hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors"
                              title="View mentee profile"
                            >
                              {pair.mentee.firstName} {pair.mentee.lastName}
                            </button>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            {pair.matchScore && (pair.matchScore.compatibility_score || pair.matchScore.compatibility_breakdown?.overall) && (() => {
                              const score = pair.matchScore.compatibility_score || pair.matchScore.compatibility_breakdown?.overall || 0;
                              const { label, color } = getCompatibilityLabel(score);
                              return (
                                <span className="text-sm">
                                  <span className="text-navy-700 dark:text-white">Compatibility: </span>
                                  <span className={`font-bold ${color}`}>{label}</span>
                                </span>
                              );
                            })()}
                            <button
                              onClick={() => handleRemoveMatch(pair)}
                              disabled={isCohortLocked}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:hover:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                              title={isCohortLocked ? lockTooltip : 'Remove match'}
                            >
                              <MdRemove className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {pair.matchReason && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 ml-12 mb-1">
                            {pair.matchReason}
                          </div>
                        )}

                        {pair.matchMetrics && pair.matchMetrics.length > 0 && (
                          <div className="ml-12 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                            <h4 className="text-xs font-semibold text-navy-700 dark:text-white mb-1">Match Highlights:</h4>
                            <ul className="space-y-0.5">
                              {pair.matchMetrics.map((metric, index) => (
                                <li key={index} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-2">
                                  <span className="text-brand-500 mt-0.5">•</span>
                                  <span>{metric}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Show unmatched mentees below the pending pairs if they exist */}
              {displayUnmatchedMentees.length > 0 && (
                <div ref={unmatchedMenteesRef} className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MdInfo className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="font-medium text-gray-800 dark:text-gray-200">
                      Unmatched {getLabel('mentee', 'Mentee')}s
                    </h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
                    {displayUnmatchedMentees.length} {getLabel('mentee', 'mentee').toLowerCase()}s could not be matched with available {getLabel('mentor', 'mentor').toLowerCase()}s due to compatibility thresholds.
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {displayUnmatchedMentees.map((mentee, index) => (
                      <div key={index} className="flex items-center justify-between bg-white dark:bg-navy-800 rounded p-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-100 dark:bg-gray-900/30 rounded-full flex items-center justify-center">
                            <MdPerson className="h-2 w-2 text-gray-600 dark:text-gray-400" />
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const user = await userDataService.fetchSingleUser(mentee.uid);
                                if (user) {
                                  setViewingUser(user);
                                  setIsProfileModalOpen(true);
                                }
                              } catch (error) {
                                console.error('Error loading user profile:', error);
                                notificationService.error('Failed to load profile');
                              }
                            }}
                            className="font-medium text-navy-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors"
                            title="View mentee profile"
                          >
                            {mentee.name}
                          </button>
                        </div>
                        <span className="text-gray-500 dark:text-gray-400">
                          {mentee.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show unmatched mentors below the unmatched mentees if they exist */}
              {displayUnmatchedMentors.length > 0 && (
                <div ref={unmatchedMentorsRef} className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MdInfo className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="font-medium text-gray-800 dark:text-gray-200">
                      Unmatched {getLabel('mentor', 'Mentor')}s
                    </h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
                    {displayUnmatchedMentors.length} {getLabel('mentor', 'mentor').toLowerCase()}s could not be matched with available {getLabel('mentee', 'mentee').toLowerCase()}s.
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {displayUnmatchedMentors.map((mentor, index) => (
                      <div key={index} className="flex items-center justify-between bg-white dark:bg-navy-800 rounded p-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-100 dark:bg-gray-900/30 rounded-full flex items-center justify-center">
                            <MdPerson className="h-2 w-2 text-gray-600 dark:text-gray-400" />
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const user = await userDataService.fetchSingleUser(mentor.uid);
                                if (user) {
                                  setViewingUser(user);
                                  setIsProfileModalOpen(true);
                                }
                              } catch (error) {
                                console.error('Error loading user profile:', error);
                                notificationService.error('Failed to load profile');
                              }
                            }}
                            className="font-medium text-navy-700 dark:text-white hover:text-green-600 dark:hover:text-green-400 hover:underline transition-colors"
                            title="View mentor profile"
                          >
                            {mentor.name}
                          </button>
                        </div>
                        <span className="text-gray-500 dark:text-gray-400">
                          {mentor.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : pendingPairs.length > 0 && recommendedSearch ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <MdSearch className="mx-auto h-10 w-10 text-gray-400 mb-3" />
              <p className="text-gray-500 text-sm">No results found for "{recommendedSearch}"</p>
              <button
                onClick={() => setRecommendedSearch('')}
                className="text-brand-500 hover:text-brand-600 text-sm mt-2"
              >
                Clear search
              </button>
            </div>
          ) : (displayUnmatchedMentees.length > 0 || displayUnmatchedMentors.length > 0) ? (
            <div className="space-y-4">
              {displayUnmatchedMentees.length > 0 && (
                <div ref={unmatchedMenteesRef} className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MdInfo className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="font-medium text-gray-800 dark:text-gray-200">
                      Unmatched {getLabel('mentee', 'Mentee')}s
                    </h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
                    {displayUnmatchedMentees.length} {getLabel('mentee', 'mentee').toLowerCase()}s could not be matched with available {getLabel('mentor', 'mentor').toLowerCase()}s due to compatibility thresholds.
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {displayUnmatchedMentees.map((mentee, index) => (
                      <div key={index} className="flex items-center justify-between bg-white dark:bg-navy-800 rounded p-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-100 dark:bg-gray-900/30 rounded-full flex items-center justify-center">
                            <MdPerson className="h-2 w-2 text-gray-600 dark:text-gray-400" />
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const user = await userDataService.fetchSingleUser(mentee.uid);
                                if (user) {
                                  setViewingUser(user);
                                  setIsProfileModalOpen(true);
                                }
                              } catch (error) {
                                console.error('Error loading user profile:', error);
                                notificationService.error('Failed to load profile');
                              }
                            }}
                            className="font-medium text-navy-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors"
                            title="View mentee profile"
                          >
                            {mentee.name}
                          </button>
                        </div>
                        <span className="text-gray-500 dark:text-gray-400">
                          {mentee.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {displayUnmatchedMentors.length > 0 && (
                <div ref={unmatchedMentorsRef} className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MdInfo className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="font-medium text-gray-800 dark:text-gray-200">
                      Unmatched {getLabel('mentor', 'Mentor')}s
                    </h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
                    {displayUnmatchedMentors.length} {getLabel('mentor', 'mentor').toLowerCase()}s could not be matched with available {getLabel('mentee', 'mentee').toLowerCase()}s.
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {displayUnmatchedMentors.map((mentor, index) => (
                      <div key={index} className="flex items-center justify-between bg-white dark:bg-navy-800 rounded p-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-100 dark:bg-gray-900/30 rounded-full flex items-center justify-center">
                            <MdPerson className="h-2 w-2 text-gray-600 dark:text-gray-400" />
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const user = await userDataService.fetchSingleUser(mentor.uid);
                                if (user) {
                                  setViewingUser(user);
                                  setIsProfileModalOpen(true);
                                }
                              } catch (error) {
                                console.error('Error loading user profile:', error);
                                notificationService.error('Failed to load profile');
                              }
                            }}
                            className="font-medium text-navy-700 dark:text-white hover:text-green-600 dark:hover:text-green-400 hover:underline transition-colors"
                            title="View mentor profile"
                          >
                            {mentor.name}
                          </button>
                        </div>
                        <span className="text-gray-500 dark:text-gray-400">
                          {mentor.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <MdSmartToy className="mx-auto h-10 w-10 text-gray-400 mb-3" />
              <p className="text-gray-500 text-sm">No matches yet</p>
            </div>
          )}
        </div>

        {/* Right Column: Documentation */}
        <div className="space-y-6 xl:col-span-1">
          <div className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm max-h-fit overflow-y-auto">
            <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MdAutoAwesome className="h-4 w-4 text-purple-500" />
                  <span className="font-medium text-navy-700 dark:text-white">AI Matching</span>
                </div>
                <p className="text-xs">Latte AI scans your cohort to spot perfect intro opportunities, matching members who can genuinely help each other grow.</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MdSchedule className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-navy-700 dark:text-white">Email Sending</span>
                </div>
                <p className="text-xs">Use 'Launch' to send connection emails. Automated scheduling coming soon.</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MdSearch className="h-4 w-4 text-green-500" />
                  <span className="font-medium text-navy-700 dark:text-white">Search & Filter</span>
                </div>
                <p className="text-xs">Search through matches by names, roles, or match reasoning to quickly find specific pairings.</p>
              </div>

              <div className="pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <MdSettings className="h-4 w-4 text-purple-500" />
                  <span className="font-medium text-navy-700 dark:text-white">Cohort Actions</span>
                </div>
                <p className="text-xs">Configure cohort settings and workflows. Example emails for each action can be found here.</p>
              </div>
            </div>

            <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
              {displayRecommendations.length > 0 && (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2 mb-2">
                    <MdInfo className="h-4 w-4 text-blue-500" />
                    <span className="font-medium text-navy-700 dark:text-white">AI Recommendations</span>
                  </div>
                  <div className="space-y-2">
                    {displayRecommendations.map((recommendation, index) => (
                      <div key={index} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-xs">
                        <p className="text-blue-800 dark:text-blue-200">
                          {recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {displayDistributionMetrics && (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2 mb-3">
                    <MdBarChart className="h-4 w-4 text-green-500" />
                    <span className="font-medium text-navy-700 dark:text-white">Distribution Metrics</span>
                  </div>
                  <div className="space-y-3">
                    {/* Summary Metrics */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Avg {getLabel('mentee', 'Mentee')}s/{getLabel('mentor', 'Mentor')}</div>
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          {displayDistributionMetrics.average_mentees_per_mentor?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Load Balance</div>
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                          {displayDistributionMetrics.load_balance_score?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>

                    {/* Standard Deviation */}
                    <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-2">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Standard Deviation</div>
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {displayDistributionMetrics.standard_deviation?.toFixed(2) || '0.00'}
                      </div>
                    </div>

                    {/* Distribution Breakdown */}
                    <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-2">
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {getLabel('mentor', 'Mentor')} Distribution
                      </div>
                      <div className="space-y-1 text-xs">
                        {[0, 1, 2, 3, 4, 5].map(count => {
                          const key = count === 1 ? 'mentors_with_1_mentee' : `mentors_with_${count}_mentees`;
                          const value = (displayDistributionMetrics as any)[key] || 0;
                          return (
                            <div key={count} className="flex justify-between items-center">
                              <span className="text-gray-600 dark:text-gray-400">
                                {count} {getLabel('mentee', 'mentee').toLowerCase()}{count !== 1 ? 's' : ''}:
                              </span>
                              <span className="font-medium text-gray-800 dark:text-gray-200">
                                {value} {getLabel('mentor', 'mentor').toLowerCase()}{value !== 1 ? 's' : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                <h4 className="font-medium text-navy-700 dark:text-white mb-2">About Matches:</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 bg-blue-100 rounded border border-blue-200 mt-1 flex-shrink-0"></div>
                    <div>
                      <div className="font-medium text-navy-700 dark:text-white">Matched</div>
                      <div className="text-gray-600 dark:text-gray-400">Mentor-mentee pairs generated by AI for this cohort. Review and use 'Launch' to send connection emails.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 bg-gray-100 rounded border border-gray-300 mt-1 flex-shrink-0"></div>
                    <div>
                      <div className="font-medium text-navy-700 dark:text-white">Unmatched</div>
                      <div className="text-gray-600 dark:text-gray-400">Mentors or mentees who couldn't be paired in this round. Follow AI Recommendations below to minimize unmatched participants by adjusting cohort composition.</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                <h4 className="font-medium text-navy-700 dark:text-white mb-2">Compatibility Ratings:</h4>
                <div className="space-y-2 text-xs">
                  <div className="text-gray-600 dark:text-gray-400 mb-3">
                    Compatibility is calculated based on professional background, shared interests, and educational alignment between mentor and mentee.
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 bg-green-100 rounded border border-green-200 mt-1 flex-shrink-0"></div>
                    <div>
                      <div className="font-medium text-green-600 dark:text-green-400">Excellent (80%+)</div>
                      <div className="text-gray-600 dark:text-gray-400">Strong alignment across multiple areas</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 bg-blue-100 rounded border border-blue-200 mt-1 flex-shrink-0"></div>
                    <div>
                      <div className="font-medium text-blue-600 dark:text-blue-400">High (60-79%)</div>
                      <div className="text-gray-600 dark:text-gray-400">Good match with solid potential</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 bg-yellow-100 rounded border border-yellow-200 mt-1 flex-shrink-0"></div>
                    <div>
                      <div className="font-medium text-yellow-600 dark:text-yellow-400">Medium (40-59%)</div>
                      <div className="text-gray-600 dark:text-gray-400">Moderate compatibility</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 bg-gray-100 rounded border border-gray-300 mt-1 flex-shrink-0"></div>
                    <div>
                      <div className="font-medium text-gray-600 dark:text-gray-400">Low (&lt;40%)</div>
                      <div className="text-gray-600 dark:text-gray-400">Limited common ground</div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Cohort Settings Modal */}
      {isCohortSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-navy-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-navy-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Cohort Settings</h3>
              <button
                onClick={() => setIsCohortSettingsOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <MdClose className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Customize your cohort details.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cohort Title
                </label>
                <input
                  type="text"
                  placeholder="e.g., Fall 2024 Cohort"
                  value={cohortTitle}
                  onChange={(e) => setCohortTitle(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  placeholder="e.g., Professional development cohort for new managers"
                  value={cohortDescription}
                  onChange={(e) => setCohortDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setIsCohortSettingsOpen(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (selectedCohortId === EXAMPLE_COHORT_ID) {
                      notificationService.warning('Cannot save settings for the example cohort. Please create a new cohort first.');
                      return;
                    }

                    if (!currentTenantId || !selectedCohortId) {
                      return;
                    }

                    // Validate that title is not empty
                    if (!cohortTitle?.trim()) {
                      notificationService.error('Cohort title is required');
                      return;
                    }

                    try {
                      const updateData: any = {
                        title: cohortTitle.trim(),
                        description: cohortDescription.trim() || null
                      };

                      await updateDoc(doc(db, 'tenants', currentTenantId, 'cohorts', selectedCohortId), updateData);

                      // Update local state
                      setCohorts(prevCohorts =>
                        prevCohorts.map(cohort =>
                          cohort.id === selectedCohortId
                            ? {
                                ...cohort,
                                title: cohortTitle.trim(),
                                description: cohortDescription.trim() || undefined
                              }
                            : cohort
                        )
                      );

                      notificationService.success('Cohort settings saved successfully!');
                      setIsCohortSettingsOpen(false);
                      console.log('✅ Cohort settings saved:', updateData);
                    } catch (error) {
                      console.error('❌ Error saving settings:', error);
                      notificationService.error('Failed to save settings. Please try again.');
                    }
                  }}
                  disabled={selectedCohortId === EXAMPLE_COHORT_ID}
                  className="flex-1 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => {
          setIsProfileModalOpen(false);
          setViewingUser(null);
        }}
        user={viewingUser}
      />

      {/* Alert Popup */}
      <AlertPopup
        isOpen={alertConfig.isOpen}
        onClose={() => setAlertConfig({ ...alertConfig, isOpen: false })}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
      />

      {/* Confirm Popup */}
      <ConfirmPopup
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText="Confirm"
        cancelText="Cancel"
        variant="default"
      />

      {/* Participant Change Warning Popup */}
      <ConfirmPopup
        isOpen={showParticipantWarning}
        onClose={cancelParticipantChange}
        onConfirm={confirmParticipantChange}
        title="Update Participants"
        message={`This cohort has ${pairs.length} existing match${pairs.length !== 1 ? 'es' : ''}. You'll need to regenerate matches after updating participants.`}
        confirmText="Continue"
        cancelText="Cancel"
        variant="default"
      />

      {/* Prompt Popup */}
      <Popup
        isOpen={promptConfig.isOpen}
        onClose={() => setPromptConfig({ ...promptConfig, isOpen: false })}
        title={promptConfig.title}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">{promptConfig.message}</p>
          <input
            type="text"
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            autoFocus
          />
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setPromptConfig({ ...promptConfig, isOpen: false });
                setPromptValue('');
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                promptConfig.onConfirm(promptValue);
                setPromptConfig({ ...promptConfig, isOpen: false });
                setPromptValue('');
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 rounded-lg transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </Popup>

    </div>
  );
};

export default AIIntros;